import type { nl } from './nl';

// English peer locale — MUST stay key-for-key in sync with nl.ts.
export const en: Record<keyof typeof nl, string> = {
  'nav.beheer': 'Administration',
  'nav.dashboard': 'Dashboard',
  'nav.users': 'Users',
  'nav.dataOwners': 'Parties',

  'env.local': 'Local',
  'env.acceptance': 'Acceptance',
  'env.production': 'Production',
  'banner.local': 'Development environment',
  'banner.acc': 'Acceptance environment',
  'banner.note': 'data here is separate from production',

  'nav.world.fuel': 'Fuel',
  'nav.world.administration': 'Administration',
  'shell.searchPlaceholder': 'Search the system…',
  'shell.home': 'Van Hooft Transport — to the start screen',
  'shell.worlds': 'Worlds',
  'shell.modules': 'Modules',
  'shell.start': 'Start',
  'shell.skipToContent': 'Skip to content',
  'shell.notifications': 'Notifications',
  'shell.userMenu': 'User menu',
  'shortcuts.search': 'Search',
  'shortcuts.world': 'Go to world 1 … 9',
  'shortcuts.module': 'Previous / next module',
  'shortcuts.move': 'Move within worlds or modules',
  'shortcuts.firstLast': 'First / last',
  'shortcuts.close': 'Close menu or search',
  'shortcuts.list': 'This list',
  'user.profile': 'Profile',
  'user.preferences': 'Preferences',
  'user.language': 'Language: English',
  'user.theme': 'Theme: {{value}}',
  'user.themeLight': 'Light',
  'user.themeDark': 'Dark',
  'user.themeSystem': 'System',
  'user.shortcuts': 'Keyboard shortcuts',
  'user.logout': 'Sign out',
  'user.releaseNotes': 'Release notes',

  // Beheer › Rechten (docs/auth.md)
  'nav.permissions': 'Permissions',
  'permissions.title': 'Permissions',
  'permissions.subtitle':
    'What each role may do. A change counts from the next click, for everyone with that role.',
  'permissions.floor':
    'Administrator may always do everything. Creating users and handing out roles, portal access and this screen stay with Administrator: handing out access is not a right you can hand out.',
  'permissions.colRight': 'Permission',
  'permissions.adminAlways': 'Administrator may always do everything',
  'permissions.pending_one': '{{count}} change, not saved yet',
  'permissions.pending_other': '{{count}} changes, not saved yet',
  'permissions.noPending':
    'Ticking a permission also ticks what it needs; unticking removes what depends on it.',
  'permissions.saved': 'Permissions saved',
  'permissions.historyTitle': 'Recent changes',
  'permissions.historyEmpty': 'Nothing changed since permissions moved into VHT (12 October 2026).',
  'permissions.by': 'by {{name}}',
  'permission.coordination': 'View orders and intake',
  'permission.coordination.hint': 'Dossiers, stations, phases and the Alfen intake',
  'permission.coordination_write': 'Update orders',
  'permission.coordination_write.hint':
    'Status, phases, documents, work items, delivery date, KLIC, reprocessing',
  'permission.groundwork': 'View groundwork reports',
  'permission.groundwork.hint': 'What Russcher sent through the portal',
  'permission.groundwork_resend': 'Send a groundwork report again',
  'permission.additional_work_notice': 'Announce extra work',
  'permission.additional_work_notice.hint':
    'A letter for Alfen and the grid operator after an inspection, as a PDF',
  'permission.additional_work_notice_send': 'Mail extra work to Alfen Transport',
  'permission.additional_work_notice_send.hint':
    'On behalf of Van Hooft, to the environment’s address',
  'permission.quotations': 'Quotations',
  'permission.quotations.hint': 'Make, issue and send; also the relations and prospects',
  'permission.quotations_settings': 'Manage quotation lists',
  'permission.quotations_settings.hint':
    'Crane types, articles and standard texts; they affect every quotation',
  'permission.fuel': 'View fuel',
  'permission.fuel.hint': 'Fuel entries, vehicles, owners, home bases, suppliers, emission factors',
  'permission.fuel_write': 'Enter and change fuel',
  'permission.staging': 'View the staging list',
  'permission.staging.hint': 'Including posting comments',
  'permission.staging_stage': 'Staging: tick off, photo and clear',
  'permission.staging_unstage_any': "Undo someone else's tick",
  'permission.staging_unstage_any.hint': 'Undoing your own tick is always possible',
  'permission.staging_sms_numbers': 'See phone numbers in the notification log',
  'permission.staging_sms_numbers.hint': 'Masked otherwise',
  'permission.loading_places_write': 'Manage loading places',
  'permission.storage': 'View floor plans',
  'permission.storage.hint': 'Including the Excel export',
  'permission.storage_write': 'Place, move and edit',
  'permission.storage_write.hint': 'Adding or deleting sends the booking mail',
  'permission.storage_admin': 'Set up floor plans and manage types',
  'permission.storage_dashboard': 'View the Storage dashboard',
  'permission.storage_dashboard.hint':
    'Stock over time, time in storage and data quality, for management',
  'permission.users': 'View users',
  'permission.users.hint': 'Changing them stays with Administrator',
  'permission.users_notifications': "Manage colleagues' notification settings",
  'permission.data_owners': 'View parties',
  'permission.data_owners_write': 'Manage parties',
  'permission.data_owners_write.hint':
    'Name, active, external codes; portal access stays with Administrator',

  // Release notes (user menu) — generated from git by scripts/release-notes.mjs
  'releaseNotes.title': 'Release notes',
  'releaseNotes.summary': '{{count}} versions · the latest is {{version}} of {{date}}',
  'releaseNotes.search': 'Search for a word, part or version',
  'releaseNotes.showOther': 'Include documentation and maintenance',
  'releaseNotes.groupNew': 'New',
  'releaseNotes.groupFixed': 'Fixed',
  'releaseNotes.groupOther': 'Documentation and maintenance',
  'releaseNotes.onlyOther': 'Only documentation and maintenance in this version.',
  'releaseNotes.noMatch': 'Nothing found.',
  'releaseNotes.scope.access': 'Permissions',
  'releaseNotes.scope.auth': 'Sign-in',
  'releaseNotes.scope.backoffice': 'Back office',
  'releaseNotes.scope.coordination': 'Coordination',
  'releaseNotes.scope.fuel': 'Fuel',
  'releaseNotes.scope.groundwork': 'Groundwork',
  'releaseNotes.scope.logistics': 'Staging list',
  'releaseNotes.scope.map': 'Map',
  'releaseNotes.scope.portal': 'Portal',
  'releaseNotes.scope.quotations': 'Quotations',
  'releaseNotes.scope.storage': 'Storage',
  'releaseNotes.scope.ui': 'Design',
  'releaseNotes.scope.users': 'Users',

  'login.title': 'Sign in',
  'login.lede': 'Welcome back. Choose how you sign in.',
  'login.sso': 'Sign in with Microsoft',
  'login.ssoNote': 'For Van Hooft employees — single sign-on.',
  'login.dividerPortal': 'or sign in to the portal',
  'login.email': 'Email address',
  'login.emailPlaceholder': 'name@company.nl',
  'login.password': 'Password',
  'login.forgot': 'Forgot password?',
  'login.portal': 'Sign in to the portal',
  'login.helpRequest': 'No portal account yet? Get in touch with us.',
  'login.brandPayoff': 'No challenge',
  'login.brandPayoffAccent': 'too great',
  'login.brandSub':
    'Leading in innovation and reliability. Our powerful, sustainable fleet guarantees clean and efficient transport and lifting.',
  'login.footLocation': 'Hoevelaken',
  'login.footFleet': 'Zero-emission fleet',
  'login.footWork': 'Transport & lifting',
  'login.mockTitle': 'Development mode',
  'login.mockNote': 'Mock auth is active. Pick a role to sign in.',
  'login.mockLogin': 'Sign in (mock)',

  'common.search': 'Search',
  'access.deniedTitle': 'This screen is not for your role',
  'access.deniedBody':
    'You have access to the platform, but not to this part of it. Not right? Ask an administrator for the right role.',
  'access.toDashboard': 'Go to the dashboard',
  'common.loading': 'Loading…',
  'common.error': 'Something went wrong.',
  'common.retry': 'Retry',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.saved': 'Saved',
  'common.cancel': 'Cancel',
  'common.create': 'Create',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.clear': 'Clear',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.actions': 'Actions',
  'common.status': 'Status',
  'common.all': 'All',
  'common.never': 'Never',
  'common.results': '{{count}} results',

  'dashboard.greeting': 'Welcome, {{name}}',
  'dashboard.subtitle': '{{date}}',
  'dashboard.asRole': 'You are working as {{roles}}.',
  'dashboard.noRole': 'No roles have been assigned to you yet. Ask an administrator.',
  'dashboard.yourWork': 'Where you work',
  'dashboard.alsoAvailable': 'Also available',
  'dashboard.noWorkTitle': 'Your module is still being built',
  'dashboard.noWorkBody':
    'The work this role exists for — planning board, quotations, coordination, fuel management — is not open yet. What you can already look at is below.',
  'dashboard.comingSoon': 'Coming soon',
  'dashboard.previewTitle': "What's coming",
  'dashboard.tile.planbord': 'Planning board',
  'dashboard.tile.planbordSub': 'Daily overview of jobs, equipment and exceptions',
  'dashboard.tile.offertes': 'Quotes',
  'dashboard.tile.offertesSub': 'Requests and calculation',
  'dashboard.tile.materieel': 'Equipment',
  'dashboard.tile.materieelSub': 'Cranes, low-loaders and the zero-emission fleet',
  'dashboard.tile.chauffeurs': 'Drivers',
  'dashboard.tile.chauffeursSub': 'Deployment and certifications',
  'dashboard.tile.co2': 'CO₂ & sustainability',
  'dashboard.tile.co2Sub': 'Reporting on zero-emission deployment',

  'users.title': 'Users',
  'users.subtitle': 'Manage internal users and their roles.',
  'users.rolesMeta': 'Only administrators assign roles',
  'users.rolesReadOnly':
    'This user\u2019s roles. Only an administrator can change them — you manage the Notifications tab.',
  'users.rolesLead':
    'Click a role on or off. Name and e-mail come from Microsoft Entra and cannot be changed here.',
  'users.roleNoModule': 'This role can only view for now',
  'users.roleNoModuleHint':
    'Dashed roles can be assigned as usual, but the work they exist for — planning board, quotations, coordination, fuel management — is not open yet.',
  'users.new': 'New user',
  'users.searchPlaceholder': 'Search by name or email…',
  'users.colName': 'Name',
  'users.colEmail': 'Email',
  'users.colRoles': 'Roles',
  'users.colStatus': 'Status',
  'users.colLastLogin': 'Last login',
  'users.empty': 'No users yet',
  'users.emptyHint': 'Add the first user or let employees sign in via SSO.',
  'users.createTitle': 'New user',
  'users.editTitle': 'Edit user',
  'users.fieldName': 'Name',
  'users.fieldEmail': 'Email address',
  'users.fieldRoles': 'Roles',
  'users.fieldActive': 'Active',
  'users.noRoles': 'No roles',

  'relations.title': 'Relations',
  'relations.subtitle': 'Where a quotation goes: from TALIS, or a prospect',
  'relations.searchPlaceholder': 'Search by name, code, place or contact',
  'relations.filterSource': 'Source',
  'relations.source.TALIS': 'TALIS',
  'relations.source.VHT': 'Prospect',
  'relations.includeInactive': 'Include inactive',
  'relations.newProspect': 'New prospect',
  'relations.editProspect': 'Edit prospect',
  'relations.prospectHint':
    'A company that is not in TALIS yet. A real relation is created in Exact; when it arrives through TALIS, you link the prospect to it.',
  'relations.lookalikes': 'Is this relation already there? Found:',
  'relations.colName': 'Name',
  'relations.colAddress': 'Address',
  'relations.colContacts': 'Contacts',
  'relations.colSource': 'Source',
  'relations.noName': '(no name)',
  'relations.empty': 'No relations yet',
  'relations.noMatch': 'No relation found for “{{q}}”',
  'relations.emptyHint': 'Relations come from TALIS. If a company is not there, create a prospect.',
  'relations.page': 'page {{page}} of {{pages}}',
  'relations.prevPage': 'Previous page',
  'relations.nextPage': 'Next page',
  'relations.talis.none':
    'No relations have been received from TALIS yet. The link through DataBridge is coming; until then there are only test data and prospects here.',
  'relations.talis.last_one': 'Last updated from TALIS: {{when}} · {{count}} relation',
  'relations.talis.last_other': 'Last updated from TALIS: {{when}} · {{count}} relations',
  'relations.talis.prospects_one': '{{count}} prospect',
  'relations.talis.prospects_other': '{{count}} prospects',
  'relations.talis.rejected':
    'The last delivery from TALIS ({{when}}) was refused. The relations are as they were before.',
  'relations.backToList': 'All relations',
  'relations.origin': 'Origin',
  'relations.fromTalis': 'From TALIS, last received {{when}}',
  'relations.fromVht': 'Prospect, created by {{who}} on {{when}}',
  'relations.talisReadOnly':
    'This relation comes from TALIS. It is changed in Exact and TALIS; the link then takes it over. You can add contacts here.',
  'relations.field.name': 'Name',
  'relations.field.street': 'Address',
  'relations.field.postcode': 'Postcode',
  'relations.field.city': 'Place',
  'relations.field.country': 'Country',
  'relations.field.phone': 'Phone',
  'relations.field.email': 'E-mail',
  'relations.emailInvalid': 'This is not an e-mail address.',
  'relations.contacts.title': 'Contacts',
  'relations.contacts.add': 'Add contact',
  'relations.contacts.edit': 'Edit contact',
  'relations.contacts.fromVht': 'VHT',
  'relations.contacts.empty': 'No contacts yet',
  'relations.contacts.emptyHint': 'Add one: a quotation is addressed to a contact.',
  'dataOwners.title': 'Parties',
  'dataOwners.subtitle':
    "Clients and relations. The code is Van Hooft's debtor code and the fixed key.",
  'dataOwners.new': 'New party',
  'dataOwners.searchPlaceholder': 'Search by name or code…',
  'dataOwners.colCode': 'Code',
  'dataOwners.colName': 'Name',
  'dataOwners.colExternal': 'External codes',
  'dataOwners.colStatus': 'Status',
  'dataOwners.empty': 'No parties yet',
  'dataOwners.emptyHint': 'Add the first client.',
  'dataOwners.createTitle': 'New party',
  'dataOwners.editTitle': 'Edit party',
  'dataOwners.fieldCode': 'Code',
  'dataOwners.fieldCodeHint': '2–20 chars: uppercase, digits, - or _',
  'dataOwners.fieldName': 'Name',
  'dataOwners.fieldActive': 'Active',

  'role.admin': 'Administrator',
  'role.planner': 'Planner',
  'role.werkvoorbereider': 'Work preparation',
  'role.logistiek': 'Logistics',
  'role.administratie': 'Administration',

  // Coordination jobs
  'nav.coordination': 'Coordination',
  'nav.orders': 'Jobs',
  'orders.title': 'Jobs',
  'orders.subtitle': 'Coordination dossiers — Alfen order intake and chain coordination.',
  'orders.searchPlaceholder': 'Search by number, Alfen order, dossier or grid operator…',
  'orders.colNumber': 'Job',
  'orders.colAlfenOrder': 'Alfen order',
  'orders.colDossier': 'Dossier',
  'orders.colBesteller': 'Grid operator',
  'orders.colStations': 'Stations',
  'orders.colStatus': 'Status',
  'orders.colCreated': 'Created',
  'orders.empty': 'No jobs yet',
  'orders.emptyHint': 'Jobs appear as soon as Alfen delivers them through intake.',
  'orders.backToList': 'Back to jobs',
  'orders.copyNumber': 'Click to copy',
  'orders.alfenOrder': 'Alfen order',
  'orders.dossier': 'dossier',
  'orders.station': 'station',
  'orders.stations': 'stations',
  'orders.changeStatus': 'Change status',
  'orders.currentStatus': 'Current status',
  'orders.newStatus': 'New status',
  'orders.pickStatus': 'Pick a status…',
  'orders.noTransitions': 'No next statuses possible from this state.',
  'orders.transitionReason': 'Reason (optional)',
  'orders.tabOverview': 'Overview',
  'orders.tabTimeline': 'Timeline',
  'orders.noStations': 'No stations in this dossier yet.',
  'orders.serviceObject': 'Service object',
  'orders.partCode': 'Part code',
  'orders.location': 'Work location',
  'orders.desiredDate': 'Desired delivery date',
  'orders.prodStatus': 'Production status',
  'orders.timeline': 'Timeline',
  'orders.timelineEmpty': 'No events yet.',
  'orders.timelineOrder': 'Dossier status changed',
  'orders.timelineStep': 'Phase changed',
  'orders.timelineRetiredStep': '{{step}} · old chain',
  'orders.parties': 'Parties',
  'orders.debiteur': 'Debtor',
  'orders.besteller': 'Grid operator',
  'orders.notLinked': 'not linked',
  'orders.contact': 'Contact',
  'orders.vendor': 'Contractor',
  'orders.actieVereist': 'Action required',
  'orders.stepNote': 'Note',
  'orders.suggest': 'Suggested',
  'orders.applySuggestion': 'Apply the derived status suggestion',
  'orders.documentsTitle': 'Documents',
  'orders.docsRequiredDone': 'required documents delivered',
  'orders.addDocument': 'Add document',
  'orders.editDocument': 'Edit document',
  'orders.docType': 'Type',
  'orders.docFileName': 'File name',
  'orders.docStatus': 'Status',
  'orders.docNoFile': 'no file name',
  'orders.noDocuments': 'No documents yet',
  'docType.CASEMAP': 'Case folder',
  'docType.SITUATIETEKENING': 'Site drawing',
  'docType.FUNDATIEADVIES': 'Foundation advice',
  'docType.FUNDERINGSRAPPORT': 'Foundation report',
  'docType.FUNDERINGSFOTOS': 'Foundation photos (PDF)',
  'docType.AARDINGSRAPPORT': 'Grounding report',
  'docType.AARDINGSFOTOS': 'Grounding photos (PDF)',
  'docType.OPLEVERDOCUMENT': 'Delivery document',
  'docType.WATERTESTRAPPORT': 'Water test report',
  'docType.WAPENINGRAPPORT': 'Reinforcement report',
  'docType.VRACHTBRIEF': 'Waybill',
  'docType.BIJLAGE': 'Attachment',
  'docType.SCHOUWFORMULIER': 'Inspection form',
  'docType.OVERIG': 'Other',
  'docStatus.PENDING': 'Pending',
  'docStatus.VERIFIED': 'Verified',
  'docStatus.REJECTED': 'Rejected',
  'docApproval.PENDING': 'To approve',
  'docApproval.VERIFIED': 'Approved',
  'docApproval.REJECTED': 'Rejected',
  'documents.meta': '{{done}} of {{total}} required delivered',
  'documents.group.PARTY': 'For the chain parties',
  'documents.group.DELIVERABLE': 'Phase deliverables',
  'documents.group.ALFEN': 'From Alfen',
  'documents.group.OTHER': 'Transport and other',
  'documents.required': 'Required',
  'documents.requiredFor': 'required for {{phase}}',
  'documents.optionalFor': 'welcome for {{phase}}',
  'documents.neededFor': 'needed for {{phases}}',
  'documents.and': 'and',
  'documents.expectedFrom': 'expected from {{party}}',
  'documents.fromParty': 'from the executor, when reporting done',
  'documents.fromRide': 'from the trip, after unloading — counts once VHT approves it',
  'documents.fromVht': 'from VHT to the party',
  'documents.casemapRequired': 'required by {{party}}',
  'documents.casemapDue': 'by {{date}} — {{weeks}} weeks before placement',
  'documents.casemapLate': 'the deadline passed on {{date}} — {{weeks}} weeks before placement',
  'documents.casemapNoDate': '{{weeks}} weeks before placement — which is not known yet',
  'documents.approve': 'Approve',
  'documents.reject': 'Reject',
  'documents.approvalHint':
    'A delivery document only counts once VHT has approved it — then it goes to the orderer.',
  'documents.reviewedBy': '{{verdict}} by {{name}} · {{at}}',
  'documents.reviewedAt': '{{verdict}} · {{at}}',
  'documents.orderTitle': 'Order attachments',
  'documents.orderEmpty': 'No order attachments yet',
  'documents.addAttachment': 'Add attachment',
  'documents.orderFoot':
    'Order attachments belong to the dossier, not to a station, and travel with the order to transport. Inspection forms are a type of their own.',

  'status.dossier.INTAKE': 'Intake',
  'status.dossier.IN_COORDINATIE': 'In coordination',
  'status.dossier.GEREED_VOOR_PLAATSING': 'Ready for placement',
  'status.dossier.GEPLAATST': 'Placed',
  'status.dossier.AFGEROND': 'Completed',
  'status.dossier.IN_DE_WACHT': 'On hold',
  'status.dossier.GEANNULEERD': 'Cancelled',

  'status.step.TE_DOEN': 'To do',
  'status.step.GEPLAND': 'Planned',
  'status.step.IN_UITVOERING': 'In progress',
  'status.step.GEREED': 'Done',
  'status.step.GEBLOKKEERD': 'Blocked',
  'status.step.NVT': 'N/A',
  'status.step.GEANNULEERD': 'Cancelled',

  'chainStep.SONDEREN': 'Probing',
  'chainStep.FUNDEREN': 'Foundation',
  'chainStep.PLAATSEN_STATION': 'Placement',
  'chainStep.AARDING': 'Grounding',
  'retiredChainStep.VOORSCHOUW': 'Pre-survey',
  'retiredChainStep.KLIC_AANVRAAG': 'KLIC request',
  'retiredChainStep.LOCATIE_UITZETTEN': 'Set out location',
  'retiredChainStep.LANDMETER_BEOORDELING': 'Surveyor assessment',
  'retiredChainStep.RIJPLATEN': 'Driving plates',
  'retiredChainStep.VERKEERSMAATREGELEN': 'Traffic measures',
  'retiredChainStep.HEKWERK_PLAATSEN': 'Install fencing',
  'retiredChainStep.GRONDWERK': 'Groundwork',
  'retiredChainStep.HEKWERK_VERWIJDEREN': 'Remove fencing',

  // KLIC (build list #6, coordination.md §10.4) — notices from Mister KLIC, via DataBridge.
  'klic.title': 'KLIC',
  'klic.meta_one': '{{count}} notice',
  'klic.meta_other': '{{count}} notices',
  'klic.link': 'Link notice',
  'klic.linkTitle': 'Link a KLIC notice',
  'klic.linkIntro':
    'Notices not yet attached to a station. Below are those whose reference points to this dossier. Search by notice number, reference or address for the rest.',
  'klic.linkSearch': 'Search by notice number, reference or address…',
  'klic.linkNone': 'No unattached notices point to this dossier.',
  'klic.linkNoMatch': 'No unattached notice found.',
  'klic.linkAction': 'Link',
  'klic.linkHere': 'Link to this station',
  'klic.matchedThisOrder': 'points to this dossier',
  'klic.unassigned':
    'Points to this dossier but is not attached to a station. Until you pick one, it counts nowhere.',
  'klic.empty': 'No KLIC notice for this station yet.',
  'klic.noNumber': 'no notice number',
  'klic.today': 'today',
  'klic.digDate': 'dig date',
  'klic.digSource.expected': 'expected',
  'klic.digSource.due': 'due by',
  'klic.footDig':
    'The dig date comes from the foundation ({{source}}: {{date}}). If it moves, this panel recalculates.',
  'klic.footNoDig':
    'No dig date yet: the foundation does not apply, is already done, or has no date.',
  'klic.footSource': 'Notices come from Mister KLIC, via DataBridge.',
  'klic.why.CLOSES_BEFORE_one':
    'Valid, but the window closes {{count}} day before the dig date of {{date}}.',
  'klic.why.CLOSES_BEFORE_other':
    'Valid, but the window closes {{count}} days before the dig date of {{date}}.',
  'klic.why.OPENS_AFTER_one': 'The window only opens {{count}} day after the dig date of {{date}}.',
  'klic.why.OPENS_AFTER_other':
    'The window only opens {{count}} days after the dig date of {{date}}.',
  'klic.why.OPEN_PRECAUTIONS_one':
    'Valid on the dig date of {{date}}, but {{count}} required precaution is still open. The grid operator requires it before digging.',
  'klic.why.OPEN_PRECAUTIONS_other':
    'Valid on the dig date of {{date}}, but {{count}} required precautions are still open. The grid operator requires them before digging.',
  'klic.why.FAILED':
    'The notice failed at Mister KLIC. There is no valid notice for the dig date of {{date}}.',
  'klic.why.NONE': 'Digging is planned for {{date}}, but there is no valid KLIC notice.',
  'klic.why.COVERED': 'Valid on the dig date of {{date}}.',
  'klic.why.PENDING': 'Requested. May still cover the dig date of {{date}}.',
  'klic.precautionsMeta_one': '{{count}} precaution, {{open}} open',
  'klic.precautionsMeta_other': '{{count}} precautions, {{open}} open',
  'klic.fileStored': 'delivery file received',
  'klic.fileAwaited': 'delivery file not yet received',
  'klic.modalTitle': 'KLIC notice',
  'klic.download': 'Delivery file (.zip)',
  'klic.fieldViewer': 'Mister KLIC',
  'klic.openViewer': 'Open in the map viewer',
  'klic.openViewerHint':
    'The notification in the Mister KLIC map viewer. You need to be signed in there.',
  'klic.fieldState': 'State',
  'klic.fieldType': 'Notice type',
  'klic.fieldReference': 'Own reference',
  'klic.fieldDescription': 'Activity description',
  'klic.fieldLocation': 'Activity location',
  'klic.fieldProject': 'Project',
  'klic.fieldValidFrom': 'Valid from',
  'klic.fieldValidUntil': 'Valid up to and including',
  'klic.fieldCreated': 'Created in Mister KLIC',
  'klic.fieldRequested': 'Sent to Kadaster',
  'klic.fieldDelivered': 'Data delivered on',
  'klic.fieldFile': 'Delivery file',
  'klic.fieldLinked': 'Link',
  'klic.fieldReceived': 'Last received',
  'klic.linkedAuto': 'automatically, from the reference · {{date}}',
  'klic.linkedBy': 'by {{name}} · {{date}}',
  'klic.notLinked': 'not linked yet',
  'klic.precautions': 'Precautions (EV)',
  'klic.precautionsNone': 'No required precautions.',
  'klic.memo': 'VHT memo',
  'klic.memoPlaceholder': 'For example: called the foundation contractor about the gas main',
  'klic.timelineLinked': 'KLIC notice {{ref}} linked',
  'klic.timelineAuto': 'automatically, from the reference',
  'klic.timelineStatus': 'KLIC notice {{ref}}',
  'klicState.GELDIG': 'Valid',
  'klicState.VERLOPEN': 'Expired',
  'klicState.MISLUKT': 'Failed',
  'klicState.GEPLAND': 'Planned',
  'klicState.AANGEVRAAGD': 'Requested',
  'klicStatus.PLANNED': 'Planned',
  'klicStatus.REQUESTED': 'Requested',
  'klicStatus.ACTIVE': 'Active',
  'klicStatus.FAILED': 'Failed',
  'klicStatus.INACTIVE': 'Inactive',
  'klicType.GRAAFMELDING': 'Excavation notice',
  'klicType.CALAMITEITENMELDING': 'Emergency notice',
  'klicType.ORIENTATIEVERZOEK': 'Orientation request',
  'klicPrecautionStatus.UNCOMPLETED': 'Open',
  'klicPrecautionStatus.IN_PROGRESS': 'In progress',
  'klicPrecautionStatus.COMPLETED': 'Completed',

  'phases.title': 'Phases',
  'phases.meta': '{{done}} of {{total}} done',
  'phases.colPhase': 'Phase · executor',
  'phases.colDue': 'Due by',
  'phases.colExpected': 'Expected',
  'phases.colCompleted': 'Reported',
  'phases.colDeliverable': 'Deliverable',
  'phases.colStand': 'State',
  'phases.details': 'Details',
  'phases.noExecutor': 'No executor assigned yet',
  'phases.notOrdered': 'Not in the Alfen order',
  'phases.transport': 'Transport VHT · internal handover, no chain party',
  'phases.refShort': 'ref',
  'phases.typeShort': 'type',
  'phases.na': 'n/a',
  'phases.overdue': 'The agreed date has passed and the phase is not done',
  'phases.foot':
    'The order is a guideline, not a block. Every change of state lands as an event in the timeline — this panel only shows where we are. Click a phase to open it.',
  'phases.edit': 'Update phase',
  'phases.noParty': 'Transport places the station itself — there is no chain party to assign.',
  'phases.deliverableOf.SONDEREN': 'Foundation advice',
  'phases.deliverableOf.FUNDEREN': 'Report + photos',
  'phases.deliverableOf.AARDING': 'Grounding report',
  'phases.deliverableOf.PLAATSEN_STATION': 'Delivery document',
  'phases.deliverableMissing': '{{label}} missing',
  'phases.deliverableAwaiting': '{{label}} to approve',
  'phases.none': '—',
  'phases.stand': 'State',
  'phases.dueDate': 'Due by',
  'phases.expectedDate': 'Expected',
  'phases.expectedPlacement': 'Expected placement',
  'phases.expectedPlacementFrom': 'derived from the {{source}}',
  'phases.expectedPlacementNone':
    'Derived: the trip, then the confirmed delivery date, then the desired one. None of the three is known.',
  'phases.completedDate': 'Reported done',
  'phases.partyReference': 'Reference of the party',
  'phases.memo': 'VHT memo',
  'phases.memoHelp': 'For the executor — they see this memo.',
  'phases.memoHelpPlacement':
    'The foundation and grounding contractors see this memo in their planning.',
  'phases.partyRemark': 'Remark from the party',
  'phases.accountability': 'Accounting for a deviation',
  'phases.accountabilityWork': 'Additional work instead of a reason code',
  'phases.reasonCode': 'Reason code',
  'phases.noReason': 'No reason code',
  'phases.foundationType': 'Foundation type',
  'phases.noFoundationType': 'Not known yet',
  'phases.additionalWork': 'Additional work',
  'phases.workDescription': 'Description',
  'phases.workAmount': 'Amount (€, excl. VAT)',
  'phases.workHelp':
    'Also possible after done. Clear the description to remove the additional work.',
  'phases.workReported': 'reported {{at}}',
  'phases.deliverable': 'Deliverable',
  'phases.inputs': 'For the party',
  'phases.inputMissing': 'not there yet',
  'phases.optional': 'optional',
  'phases.notYetDelivered': 'not delivered yet',
  'phases.missingAfterDone': 'missing — the phase is done, its deliverable is not',
  'phases.register': 'Register',
  'phases.whyTypeOk':
    'Reporting done is allowed: the foundation type is {{type}}. With NNTB the system blocks it until the type is known.',
  'phases.whyNntb':
    'Reporting done is blocked: the foundation type is NNTB. First it has to be known how the foundation is made.',
  'phases.whyNoType': 'Reporting done needs a foundation type — sand, piles or stabilisation.',
  'phases.needsCompletedDate': 'Reporting done needs the day it was done.',
  'phases.completedInFuture': 'That day is still in the future.',
  'phases.otherReasonNeedsRemark':
    '"Other reason" needs an explanation in the remark from the party.',
  'phases.amountInvalid': 'The amount is not a valid amount.',
  'phases.workNeedsDescription': 'An amount without a description is not additional work.',
  'phases.manualFeedback':
    'The party does not report back by itself yet. Register here what they pass on.',
  'foundationType.ZAND': 'Sand foundation',
  'foundationType.PALEN': 'Pile foundation',
  'foundationType.STABIL': 'Stabilisation foundation',
  'foundationType.NNTB': 'NNTB · to be determined',
  'delayReason.DOCUMENTEN_TE_LAAT': 'Required documents late',
  'delayReason.LOCATIE_TE_LAAT_GEWIJZIGD': 'Location changed too late',
  'delayReason.BOUWPLAATS_ONGESCHIKT': 'Site unsuitable',
  'delayReason.TOELEVERING_TE_LAAT': 'Supply late',
  'delayReason.VERTRAGING_PRODUCTIE': 'Delay during production',
  'delayReason.VERTRAGING_SONDATIE': 'Delay during probing',
  'delayReason.VERTRAGING_FUNDATIE': 'Delay during foundation',
  'delayReason.VERTRAGING_TRANSPORT': 'Delay during transport',
  'delayReason.VERZOEK_KLANT': 'Customer request',
  'delayReason.ANDERE_REDEN': 'Other reason',

  'workItems.title': 'Work items',
  'workItems.meta': '{{setOut}} of {{total}} set out',
  'workItems.metaNotNeeded': '{{count}} not needed',
  'workItems.setOut': 'Set out',
  'workItems.setOutTitle': 'Set out a work item',
  'workItems.notSetOut': 'Not set out yet',
  'workItems.setOutBy': 'Set out {{date}} by {{name}}',
  'workItems.setOutOn': 'Set out {{date}}',
  'workItems.notNeededBy': 'Not needed · decided by {{name}} on {{date}}',
  'workItems.notNeededOn': 'Not needed · decided on {{date}}',
  'workItems.nvt': 'N/A',
  'workItems.twoLegs': 'one work item, two legs',
  'workItems.plannedOn': 'planned {{date}}',
  'workItems.doneOn': 'done {{date}}',
  'workItems.daysOpen_one': '{{count}} day open',
  'workItems.daysOpen_other': '{{count}} days open',
  'workItems.foot':
    'A work item only exists once someone sets it out. Dashed = not set out yet; grey = explicitly not needed, with who decided. The status comes from transport.',
  'workItems.kind': 'Kind',
  'workItems.note': 'Note',
  'workItems.notePlaceholder': 'What is being set out — or why it is not needed',
  'workItems.notNeeded': 'Not needed',
  'workItems.setOutAnyway': 'Set out after all',
  'workItems.legStatus': 'Status',
  'workItems.legDate': 'Date',
  'workItems.executor': 'Carried out by',
  'workItems.executorPlaceholder': 'Own crew or a third party',
  'workItems.manualFeedback':
    'Transport does not report the status back by itself yet. Record here what planning tells you.',
  'workItems.dateRequired': 'A leg that is planned or done needs a date.',
  'workItems.notNeededBlocked':
    'Not needed is no longer possible: transport has already planned or done it. Cancel the legs first.',
  'workItems.timelineSetOut': '{{kind}} set out',
  'workItems.timelineNotNeeded': '{{kind}} not needed',
  'workItems.timelineLeg': '{{kind}} · {{leg}}',
  'workItemKind.VOORSCHOUW': 'Pre-inspection',
  'workItemKind.LOCATIE_UITZETTEN': 'Staking out the location',
  'workItemKind.RIJPLATEN': 'Road plates',
  'workItemKind.VERKEERSMAATREGELEN': 'Traffic measures (TVM)',
  'workItemKind.BOUWHEKKEN': 'Construction fencing',
  'workItemKind.GRONDWERK': 'Groundwork',
  'workItemLeg.UITVOEREN': 'Carry out',
  'workItemLeg.PLAATSEN': 'Place',
  'workItemLeg.OPHALEN': 'Collect',
  'workItemLegStatus.NIET_GEPLAND': 'Not planned',
  'workItemLegStatus.GEPLAND': 'Planned',
  'workItemLegStatus.UITGEVOERD': 'Done',
  'workItemLegStatus.GEANNULEERD': 'Cancelled',
  'workItemStand.NIET_GEPLAND': 'Not planned',
  'workItemStand.GEPLAND': 'Planned',
  'workItemStand.OP_LOCATIE': 'On site',
  'workItemStand.UITGEVOERD': 'Done',
  'workItemStand.GEANNULEERD': 'Cancelled',

  // Intake queue
  'nav.intake': 'Intake',
  'intake.title': 'Intake',
  'intake.subtitle': 'Order intake from Alfen — messages, processing and reprocessing.',
  'intake.searchPlaceholder': 'Search by message id…',
  'intake.colId': 'Message id',
  'intake.colType': 'Type',
  'intake.colStatus': 'Status',
  'intake.colOrder': 'Job',
  'intake.colReceived': 'Received',
  'intake.empty': 'No messages yet',
  'intake.emptyHint': 'Messages appear as soon as Alfen delivers orders.',
  'intake.status.RECEIVED': 'Received',
  'intake.status.PROCESSED': 'Processed',
  'intake.status.DUPLICATE': 'Duplicate',
  'intake.status.FAILED': 'Failed',
  'intake.details': 'Details',
  'intake.replay': 'Reprocess',
  'intake.detailTitle': 'Intake message',
  'intake.rawPayload': 'Payload',
  'intake.errorLabel': 'Error',
  'intake.correlationId': 'Correlation id',
  'intake.linkedOrder': 'Linked job',

  // ── Fuel management (fuel) — Phase A: masterdata ──
  'nav.fuel': 'Fuel management',
  'nav.fuelEntries': 'Fuel entry',
  'nav.fuelVehicles': 'Vehicles',
  'nav.fuelOwners': 'Owners',
  'nav.fuelHomebases': 'Homebases',
  'nav.fuelSuppliers': 'Suppliers',
  'nav.fuelEmissionFactors': 'Emission factors',

  'fuel.saved': 'Saved',
  'fuel.fieldStreet': 'Street and number',
  'fuel.fieldPostcode': 'Postcode',
  'fuel.fieldPlace': 'Town',
  'fuel.fieldEmail': 'Email address',
  'fuel.fieldPhone': 'Phone',

  'fuel.fuelType.DIESEL_B7': 'Diesel B7',
  'fuel.fuelType.HVO100': 'HVO100',
  'fuel.fuelType.BENZINE_E10': 'Petrol E10',
  'fuel.fuelType.ADBLUE': 'AdBlue',
  'fuel.fuelType.ELEKTRA': 'Electric',

  // Vehicles
  'fuel.vehicles.title': 'Vehicles',
  'fuel.vehicles.subtitle': 'Vehicle masterdata for fuel entry, consumption and CO₂.',
  'fuel.vehicles.new': 'New vehicle',
  'fuel.vehicles.searchPlaceholder': 'Plate, name or code…',
  'fuel.vehicles.allFuels': 'All fuels',
  'fuel.vehicles.empty': 'No vehicles yet',
  'fuel.vehicles.emptyHint': 'Add a vehicle, or wait for the TMS feed.',
  'fuel.vehicles.backToList': 'Back to vehicles',
  'fuel.vehicles.colCode': 'VH code',
  'fuel.vehicles.colPlate': 'Plate',
  'fuel.vehicles.colName': 'Name',
  'fuel.vehicles.colCategory': 'Category',
  'fuel.vehicles.colOwner': 'Ownership',
  'fuel.vehicles.colFuel': 'Fuel',
  'fuel.vehicles.colInUse': 'In use',
  'fuel.vehicles.colRdw': 'RDW',
  'fuel.vehicles.rdwChecked': 'Retrieved',
  'fuel.vehicles.rdwUnchecked': 'Not retrieved',
  'fuel.vehicles.createTitle': 'New vehicle',
  'fuel.vehicles.fieldPlate': 'Licence plate',
  'fuel.vehicles.fieldPlateHint': 'Dashes are fine — the plate is normalised.',
  'fuel.vehicles.fieldName': 'Name',
  'fuel.vehicles.fieldCode': 'VH code',
  'fuel.vehicles.fieldCodeHint': 'Equipment code from TALIS, if known.',
  'fuel.vehicles.fieldGroup': 'Group',
  'fuel.vehicles.fieldOwner': 'Owner',
  'fuel.vehicles.noOwner': 'No owner',
  'fuel.vehicles.fieldHomebase': 'Homebase',
  'fuel.vehicles.noHomebase': 'No homebase',
  'fuel.vehicles.chooseHomebase': 'Choose a homebase…',
  'fuel.vehicles.tab.algemeen': 'General',
  'fuel.vehicles.tab.adblue': 'AdBlue',
  'fuel.vehicles.tab.broncodes': 'Source codes',
  'fuel.vehicles.tab.homebase': 'Homebase',
  'fuel.vehicles.tab.rdw': 'RDW',
  'fuel.vehicles.fieldFuels': 'Fuels',
  'fuel.vehicles.fieldFuelsHint': 'Which fuels this vehicle takes — drives the entry choices.',
  'fuel.vehicles.fieldTankVolume': 'Max. tank volume',
  'fuel.vehicles.fieldNorm': 'Norm consumption',
  'fuel.vehicles.fieldNormHint': 'Reference for the consumption check during entry.',
  'fuel.vehicles.fieldTolerance': 'Tolerance',
  'fuel.vehicles.fieldToleranceHint': 'Deviation from the norm before a warning is raised.',
  'fuel.vehicles.fieldInUseFrom': 'In use from',
  'fuel.vehicles.fieldInUseTo': 'End of use',
  'fuel.vehicles.adBlueExplainer':
    'AdBlue is a reducing agent, not a fuel: emissions during use (TTW) are zero. The odometer reading of an AdBlue entry defaults to the diesel entry on the same day.',
  'fuel.vehicles.fieldTanksAdBlue': 'This vehicle takes AdBlue',
  'fuel.vehicles.fieldAdBlueNorm': 'AdBlue norm',
  'fuel.vehicles.fieldAdBlueNormHint': 'Expected AdBlue use as a percentage of the diesel.',
  'fuel.vehicles.sourceTitle': 'Origin and source codes',
  'fuel.vehicles.sourceExplainer':
    'These fields come from the source system and are not edited here. VHT reads them; with each strangler step VHT becomes the writer.',
  'fuel.vehicles.fieldSource': 'Source',
  'fuel.vehicles.fieldExternalRef': 'Source reference',
  'fuel.vehicles.fieldGroupCode': 'Group code',
  'fuel.vehicles.fieldTypeTalis': 'Type (TALIS)',
  'fuel.vehicles.fieldFuelTalis': 'Fuel (TALIS)',
  'fuel.vehicles.fieldSnapshot': 'Source snapshot',
  'fuel.vehicles.snapshotPresent': 'Present',
  'fuel.vehicles.snapshotAbsent': 'No snapshot yet',
  'fuel.vehicles.fieldCreated': 'Created',
  'fuel.vehicles.fieldUpdated': 'Last changed',
  'fuel.vehicles.move': 'Move',
  'fuel.vehicles.moveTitle': 'Move vehicle',
  'fuel.vehicles.fieldMoveFrom': 'Move effective',
  'fuel.vehicles.fieldMoveFromHint': 'The current assignment closes on this date.',
  'fuel.vehicles.noAssignments': 'No homebase history yet',
  'fuel.vehicles.noAssignmentsHint': 'Assign the vehicle to a homebase.',
  'fuel.vehicles.rdwTitle': 'RDW data',
  'fuel.vehicles.rdwFetch': 'Retrieve from RDW',
  'fuel.vehicles.rdwFetching': 'Retrieving…',
  'fuel.vehicles.rdwCheckedAt': 'Last retrieved {{at}}',
  'fuel.vehicles.rdwNeverChecked': 'Not retrieved yet',
  'fuel.vehicles.rdwNotFound': 'The RDW register does not know plate {{plate}}.',
  'fuel.vehicles.rdwNoChanges': 'Retrieved — the data was already up to date.',
  'fuel.vehicles.rdwUpdated': 'Retrieved — {{count}} field(s) updated.',
  'fuel.vehicles.rdwExplainer':
    'This data comes from the RDW open register and is looked up by plate. Where RDW has no value, the existing one is kept.',
  'fuel.vehicles.fieldBrand': 'Make',
  'fuel.vehicles.fieldModel': 'Model name',
  'fuel.vehicles.fieldCategory': 'Vehicle category',
  'fuel.vehicles.fieldEuronorm': 'Euro standard',
  'fuel.vehicles.fieldEmptyMass': 'Empty mass',
  'fuel.vehicles.fieldMaxMass': 'Permitted max. mass',
  'fuel.vehicles.fieldPayload': 'Payload',
  'fuel.vehicles.fieldFirstAdmission': 'First admission',
  'fuel.vehicles.fieldFuelRdw': 'Fuel (RDW)',

  // Owners
  'fuel.owners.title': 'Owners',
  'fuel.owners.new': 'New owner',
  'fuel.owners.searchPlaceholder': 'Name, town or CoC…',
  'fuel.owners.empty': 'No owners yet',
  'fuel.owners.emptyHint': 'Add the first owner.',
  'fuel.owners.colName': 'Name',
  'fuel.owners.colType': 'Type',
  'fuel.owners.colKvk': 'CoC',
  'fuel.owners.colPlace': 'Town',
  'fuel.owners.colVehicles': 'Vehicles',
  'fuel.owners.createTitle': 'New owner',
  'fuel.owners.editTitle': 'Edit owner',
  'fuel.owners.fieldName': 'Company name',
  'fuel.owners.fieldType': 'Owner type',
  'fuel.owners.fieldTypeHint': 'For example owned, leased or charter.',
  'fuel.owners.fieldKvk': 'CoC number',
  'fuel.owners.fieldContact': 'Contact person',

  // Homebases
  'fuel.homebases.title': 'Homebases',
  'fuel.homebases.editThis': 'Edit homebase',
  'fuel.homebases.new': 'New homebase',
  'fuel.homebases.searchPlaceholder': 'Name or town…',
  'fuel.homebases.empty': 'No homebases yet',
  'fuel.homebases.emptyHint': 'Add the first homebase.',
  'fuel.homebases.colName': 'Name',
  'fuel.homebases.colType': 'Type',
  'fuel.homebases.colAddress': 'Address',
  'fuel.homebases.colCoords': 'Coordinates',
  'fuel.homebases.colVehiclesNow': 'Vehicles now',
  'fuel.homebases.coordsMissing': 'Missing',
  'fuel.homebases.coordsMissingHint': 'Coordinates are needed for the CO₂ context.',
  'fuel.homebases.coordsHelp': 'Decimal degrees (WGS84), for example 52,164 and 5,452.',
  'fuel.homebases.createTitle': 'New homebase',
  'fuel.homebases.editTitle': 'Edit homebase',
  'fuel.homebases.fieldName': 'Name',
  'fuel.homebases.fieldType': 'Type',
  'fuel.homebases.fieldTypeHint': 'For example site, depot or home address.',
  'fuel.homebases.fieldLat': 'Latitude',
  'fuel.homebases.fieldLon': 'Longitude',
  'fuel.homebases.assignmentsFor': 'Assignments — {{name}}',
  'fuel.homebases.colVehicle': 'Vehicle',
  'fuel.homebases.colFrom': 'From',
  'fuel.homebases.colTo': 'To',
  'fuel.homebases.current': 'Current',
  'fuel.homebases.past': 'History',
  'fuel.homebases.mapTitle': 'Map',
  'fuel.homebases.mapHint': 'Click a marker to select the homebase.',
  'fuel.homebases.mapNoPoints':
    'No homebase has coordinates yet — enter them, or look them up from the address.',
  'fuel.homebases.mapUnavailable':
    'The map is not configured for this environment. Without a HERE key the rest of the screen still works.',
  'fuel.homebases.geocode': 'Find coordinates',
  'fuel.homebases.geocodeBusy': 'Searching…',
  'fuel.homebases.geocodeNeedsAddress': 'Enter a street, postcode or town first.',
  'fuel.homebases.geocodeNoResult':
    'No address found. Check the spelling, or enter the coordinates by hand.',
  'fuel.homebases.geocodeExact': 'Exact',
  'fuel.homebases.geocodeApprox': 'Approximate',
  'fuel.homebases.noAssignments': 'No assignments',
  'fuel.homebases.noAssignmentsHint': 'No vehicles are stationed here yet.',

  // Suppliers
  'fuel.suppliers.title': 'Suppliers',
  'fuel.suppliers.subtitle': 'Fuel suppliers — the picklist on a fuel entry.',
  'fuel.suppliers.new': 'New supplier',
  'fuel.suppliers.searchPlaceholder': 'Name or town…',
  'fuel.suppliers.empty': 'No suppliers yet',
  'fuel.suppliers.emptyHint': 'Add the first supplier.',
  'fuel.suppliers.colName': 'Name',
  'fuel.suppliers.colType': 'Type',
  'fuel.suppliers.createTitle': 'New supplier',
  'fuel.suppliers.editTitle': 'Edit supplier',
  'fuel.suppliers.fieldName': 'Name',
  'fuel.suppliers.fieldType': 'Type',
  'fuel.suppliers.fieldTypeHint': 'For example filling station, own tank or fuel card.',

  // Emission factors
  'fuel.emissionFactors.title': 'Emission factors',
  'fuel.emissionFactors.searchPlaceholder': 'Description or code…',
  'fuel.emissionFactors.allCategories': 'All categories',
  'fuel.emissionFactors.operationalOnly': 'Operational fuels only',
  'fuel.emissionFactors.empty': 'No emission factors',
  'fuel.emissionFactors.emptyHint': 'Run the seed to fill the factor catalogue.',
  'fuel.emissionFactors.colDescription': 'Description',
  'fuel.emissionFactors.colCode': 'Code',
  'fuel.emissionFactors.colCategory': 'Category',
  'fuel.emissionFactors.colYear': 'Year',
  'fuel.emissionFactors.colTtw': 'TTW',
  'fuel.emissionFactors.colWtt': 'WTT',
  'fuel.emissionFactors.colWtw': 'WTW',
  'fuel.emissionFactors.colUnit': 'Unit',
  'fuel.emissionFactors.colValid': 'Valid',
  'fuel.emissionFactors.colSource': 'Source',
  'fuel.emissionFactors.editTitle': 'Correct emission factor',
  'fuel.emissionFactors.wtwMismatch': 'WTW must equal TTW + WTT ({{sum}}).',
  'fuel.emissionFactors.sourceNote': 'Source: COFRET · emissiefactoren.nl — updated yearly.',

  // Fuel entry (Fase B)
  'fuel.entries.title': 'Fuel entry',
  'fuel.entries.subtitle': 'Record tankings with a mandatory odometer check.',
  'fuel.entries.new': 'New entry',
  'fuel.entries.bulk': 'Bulk entry',
  'fuel.entries.searchVehicle': 'Search a vehicle…',
  'fuel.entries.allVehicles': 'All vehicles',
  'fuel.entries.empty': 'No entries yet',
  'fuel.entries.emptyHint': 'Record the first tanking, or enter a series in bulk.',
  'fuel.entries.colDate': 'Date',
  'fuel.entries.colVehicle': 'Vehicle',
  'fuel.entries.colFuel': 'Fuel',
  'fuel.entries.colAmount': 'Amount',
  'fuel.entries.colPrevKm': 'Previous reading',
  'fuel.entries.colKm': 'Odometer',
  'fuel.entries.colDelta': 'Δ km',
  'fuel.entries.colL100': 'L/100',
  'fuel.entries.colSupplier': 'Supplier',
  'fuel.entries.createTitle': 'New entry',
  'fuel.entries.chooseVehicle': 'Choose a vehicle…',
  'fuel.entries.noSupplier': 'No supplier',
  'fuel.entries.note': 'Note',
  'fuel.entries.kmRequired': 'Required — a tanking without an odometer reading does not exist.',
  'fuel.entries.adBlueKmHint': 'Taken from the diesel entry on the same day; adjustable.',
  'fuel.entries.saveAnyway': 'Save anyway',
  'fuel.entries.confirmOverride': 'Confirm and save',
  'fuel.entries.overrideWarning':
    'This entry conflicts with the vehicle’s series. Saving records that on the row and in the audit log.',
  'fuel.entries.override': 'Overridden',
  'fuel.entries.overrideHint': 'Saved after review by the administrator, despite a block.',
  'fuel.entries.bulkTitle': 'Bulk entry',
  'fuel.entries.bulkHint':
    'Fill in several rows; validation runs at the end of the batch, not per row. Everything is saved at once — or nothing is.',
  'fuel.entries.addRow': 'Add row',
  'fuel.entries.bulkSave': 'Save {{count}} rows',
  'fuel.entries.bulkOverride': 'Force {{count}} through',
  'fuel.entries.rowLabel': 'Row {{n}}',

  'fuel.unit.LITER': 'L',
  'fuel.unit.KWH': 'kWh',
  'fuel.unit.KG': 'kg',
  'fuel.unit.NM3': 'Nm³',

  'fuel.finding.KM_NOT_INCREASING':
    'Odometer {{kmStand}} is lower than the previous entry ({{previousKm}} on {{previousDate}}). The meter cannot run backwards.',
  'fuel.finding.KM_BELOW_PREVIOUS':
    'Odometer {{kmStand}} falls below the earlier entry of {{previousKm}} ({{previousDate}}).',
  'fuel.finding.KM_ABOVE_NEXT':
    'Odometer {{kmStand}} exceeds the later entry of {{nextKm}} ({{nextDate}}).',
  'fuel.finding.KM_DUPLICATE':
    'Same odometer ({{kmStand}}) as the entry of {{previousDate}} — was the vehicle driven in between?',
  'fuel.finding.CONSUMPTION_ABOVE_NORM':
    'Consumption {{l100}} L/100 km is above the norm of {{norm}} + {{tolerantiePct}}% (over {{deltaKm}} km).',
  'fuel.finding.CONSUMPTION_IMPLAUSIBLE':
    'Consumption {{l100}} L/100 km is implausible: {{aantal}} over {{deltaKm}} km. Check the odometer.',

  // ── Klaarzetlijst logistiek (docs/logistics.md) ──
  'nav.logistics': 'Logistics',
  'nav.staging': 'Staging list',

  'staging.title': 'Staging',
  'staging.titleFor': 'Staging for {{date}}',
  'staging.subtitle': 'Stage materials per loading place.',
  'staging.lastImport': 'Last export {{at}}',
  'staging.pickDay': 'Pick a day',
  'staging.chooseDate': 'Choose date',
  'staging.stockNever': 'no stock information yet',
  'staging.stockAt': 'updated {{at}}',
  'staging.stockStale': 'updated {{at}} — outdated',
  'staging.pastReadOnly':
    'This day is in the past. Staging can no longer be changed; comments are still allowed.',
  'staging.noExport': 'No export has been received for this day yet.',
  'staging.noExportHint':
    'The base list arrives from TALIS at 15:30, two working days before the load day.',
  'staging.noMatches': 'No lines match the filters.',
  'staging.clearFilters': 'Clear filters',

  'staging.tileTodo': 'To do',
  'staging.tileStaged': 'Staged',
  'staging.tileNew': 'New since base export',
  'staging.tileRemoved': 'Expired',
  'staging.tileInStock': 'In stock (WMS)',
  'staging.toClear': '{{count}} still to clear away',
  'staging.notInStockCount': '{{count}} not yet arrived',
  'staging.bookedOutCount': '{{count}} booked out',

  'staging.updateLog': 'Update log ({{count}})',
  'staging.baseList': 'Base list',
  'staging.updateN': 'Update {{n}}',
  'staging.linesCount': '{{count}} lines',

  'staging.filterProgress': 'Filter by progress',
  'staging.filterFoundation': 'Filter by foundation status',
  'staging.filter.all': 'All',
  'staging.filter.todo': 'To do',
  'staging.filter.staged': 'Staged',
  'staging.filter.mutations': 'Changes',
  'staging.filter.notin': 'Not yet arrived',
  'staging.foundation.requested': 'Requested',
  'staging.foundation.inProgress': 'In progress',
  'staging.foundation.onHold': 'On hold',
  'staging.foundation.done': 'Done',
  'staging.foundationAll': 'Foundation: all',
  // Alfen production status (TBL_STATUS, 11–91) — features/logistics/productionStatus.ts
  'staging.production.11': 'No costing yet',
  'staging.production.15': 'Ready for engineering',
  'staging.production.21': 'Costing not approved',
  'staging.production.22': 'Costing not approved, hours/material booked',
  'staging.production.31': 'Costing approved',
  'staging.production.41': 'Ready for planning',
  'staging.production.45': 'Planning done',
  'staging.production.51': 'Order still to pick',
  'staging.production.52': 'Pick list printed',
  'staging.production.53': 'Picking done',
  'staging.production.54': 'Picked – on hold',
  'staging.production.61': 'In production',
  'staging.production.62': 'Ready for inspection',
  'staging.production.63': 'Outside, not inspected',
  'staging.production.64': 'Moved, not inspected',
  'staging.production.71': 'Out of production',
  'staging.production.72': 'Sales line moved',
  'staging.production.81': 'Post-calculation',
  'staging.production.90': 'Check before history',
  'staging.production.91': 'In history',
  'staging.searchPlaceholder': 'Search party, serial, description…',

  'staging.unnamedPlace': 'Loading place {{code}}',
  'staging.stagedOf': '{{staged}} of {{total}} staged',
  'staging.badgeNew': 'new',
  'staging.badgeRemoved': 'expired',

  'staging.colParty': 'Party identity',
  'staging.colSerial': 'Serial number',
  'staging.colDebtor': 'Debtor',
  'staging.colOrderer': 'Orderer',
  'staging.colOrderNr': 'Order no.',
  'staging.colProduction': 'Production status',
  'staging.colFoundation': 'Foundation status',
  'staging.colFoundationDate': 'Found. date',
  'staging.colFoundationExpected': 'Found. expected',
  'staging.colStaged': 'Staged',
  'staging.colPhoto': 'Photo',
  'staging.colComments': 'Comments',
  'staging.colLocation': 'Loading place',

  'staging.newSince': 'New since {{at}}',
  'staging.removedAt': 'Expired {{at}}',
  'staging.wasStagedClearIt': 'was already staged → clear away',
  'staging.doNotStage': 'do not stage',
  'staging.stageIt': 'Stage',
  'staging.staged': 'Staged',
  'staging.clearAway': 'Clear away',
  'staging.clearedBy': 'Cleared away by {{who}}',
  'staging.undo': 'Undo tick',
  'staging.undoConfirm': 'Yes, undo',
  'staging.inStock': 'In stock in WMS',
  'staging.notInStock': 'not yet arrived',
  'staging.notInStockHint': 'Not found in WMS — still has to arrive',
  'staging.bookedOut': 'booked out',
  'staging.bookedOutHint': 'Was in stock (last seen {{at}}) and has been booked out of the WMS',
  'staging.noPhoto': 'no photo',
  'staging.openPhoto': 'View photo large',
  'staging.photoOf': 'Photo of {{party}}',

  'staging.comments': 'Notes',
  'staging.noComments': 'No comments yet.',
  'staging.commentPlaceholder': 'Add a comment…',
  'staging.commentAdd': 'Post',

  'staging.photoTitle': 'Photos for staging',
  'staging.photoRequiredHint':
    'At least one photo is required, at most {{max}}. Take them with the camera or choose files, then confirm.',
  'staging.photoChoose': 'Take or choose a photo',
  'staging.photoAdd': 'Another photo',
  'staging.photoRemove': 'Remove photo',
  'staging.photoSaved': 'Already saved',
  'staging.photoCount': '{{count}} of {{max}} photos',
  'staging.photoMaxReached': 'At most {{max}} photos per line — the rest were not added.',
  'staging.photoPreviewAlt': 'Chosen photo',
  'staging.photoReadFailed': 'That photo could not be read. Try another.',
  'staging.confirmStaged': 'Confirm staged',
  'staging.confirming': 'Working…',
  'staging.uploadingPhoto': 'Uploading photo {{at}} of {{of}}…',

  // ── Loading place management (docs/logistics.md §3.1) ──
  'nav.loadingPlaces': 'Loading places',

  'loadingPlaces.title': 'Loading places',
  'loadingPlaces.subtitle':
    'The loading places staging runs for. Code, name and address come from TALIS; the short name is ours to write.',
  'loadingPlaces.new': 'Add loading place',
  'loadingPlaces.empty': 'No loading places yet',
  'loadingPlaces.emptyHint': "The client's ten codes belong here.",

  'loadingPlaces.colCode': 'Code',
  'loadingPlaces.colName': 'Name from TALIS',
  'loadingPlaces.colAddress': 'Address from TALIS',
  'loadingPlaces.colShortName': 'Short name',
  'loadingPlaces.colOrder': 'Order',
  'loadingPlaces.noNameYet': 'no export received yet',
  'loadingPlaces.noShort': 'not filled in yet',

  'loadingPlaces.missingShort_one': '{{count}} loading place still has no short name',
  'loadingPlaces.missingShort_other': '{{count}} loading places still have no short name',
  'loadingPlaces.missingShortHint':
    'Without short names an SMS about two loading places falls back to “(2 locaties)” — dropping exactly what the recipient signed up for.',

  'loadingPlaces.createTitle': 'Add loading place',
  'loadingPlaces.editTitle': 'Edit loading place',
  'loadingPlaces.fieldCode': 'Code',
  'loadingPlaces.fieldCodeHelp':
    'The TALIS loading/unloading place code, not the internal id. Cannot be changed later.',
  'loadingPlaces.fieldName': 'Name from TALIS',
  'loadingPlaces.fieldAddress': 'Address from TALIS',
  'loadingPlaces.nameFromExport':
    'Name and address arrive with every export and are refreshed on each intake; there is nothing to fill in here.',
  'loadingPlaces.fieldShortName': 'Short name',
  'loadingPlaces.fieldShortNamePlaceholder': 'de Wieken',
  'loadingPlaces.fieldShortNameHelp':
    'What this loading place is called in an SMS. Keep it short — a notification has 160 characters for everything together.',
  'loadingPlaces.fieldOrder': 'Order',
  'loadingPlaces.fieldOrderHelp': 'Sets the position in lists and pickers. Low sorts first.',

  'loadingPlaces.previewTitle': 'How a notification reads',
  'loadingPlaces.previewChars': '{{length}} of {{max}} characters',
  'loadingPlaces.previewShortened': 'shortened to stay within one message',
  'loadingPlaces.previewFallback.name': 'still using the name from TALIS',
  'loadingPlaces.previewFallback.code': 'still using the code — TALIS has not supplied a name yet',

  // ── Preferences + notification settings (docs/logistics.md §12.4) ──
  'preferences.title': 'Preferences',
  'preferences.subtitle': 'Your personal settings. They apply to you only.',

  'users.backToList': 'Back to users',
  'users.tab.gegevens': 'Details',
  'users.tab.meldingen': 'Notifications',

  'notifications.title': 'Notifications',
  'notifications.lead':
    'Get a message as soon as a new staging list or a change comes in, so you do not have to keep checking yourself.',
  'notifications.editingFor': "You are editing {{name}}'s settings, not your own.",

  'notifications.channelTitle': 'Channel',
  'notifications.channelMeta': 'WhatsApp · SMS',
  'notifications.enabled': 'Send me a notification when something changes',
  'notifications.enabledHint': 'Via WhatsApp, or by SMS if that does not work.',
  'notifications.mobile': 'Mobile number',
  'notifications.mobileHelp':
    'Dutch 06 number, for WhatsApp and SMS. Required once notifications are on.',

  'notifications.eventsTitle': 'About what',
  'notifications.eventsMeta': 'Four events',
  'notifications.event.onBaseList': "Tomorrow's list has arrived",
  'notifications.event.onLinesAdded': 'New lines in an update',
  'notifications.event.onLinesRemoved': 'Dropped lines in an update',
  'notifications.event.onNotInStock': 'Lines not found in the WMS',
  'notifications.eventHint.onBaseList':
    'News of the day — this one ignores your loading-place selection.',
  'notifications.eventHint.onLinesAdded': 'Material has been added that needs staging.',
  'notifications.eventHint.onLinesRemoved': 'Material has been taken off the list.',
  'notifications.eventHint.onNotInStock':
    'Based on the last stock snapshot, which can be up to fifteen minutes old.',

  'notifications.placesTitle': 'For which loading places',
  'notifications.placesMeta': 'Loading places',
  'notifications.placesAll': 'All loading places',
  'notifications.placesCount': '{{count}} selected',
  'notifications.placesLead':
    'Select nothing and your notifications cover every loading place. To receive nothing, switch notifications off above.',
  'notifications.placesEmpty': 'No loading places are known yet.',

  'notifications.quietTitle': 'Quiet hours',
  'notifications.quietMeta': 'Local time',
  'notifications.quietLead':
    'Your phone stays silent between these times. The period may run past midnight. Missed notifications are not resent — they are in the update log.',
  'notifications.quietFrom': 'From',
  'notifications.quietTo': 'To',

  'notifications.error.invalid_mobile': 'That is not a Dutch 06 number.',
  'notifications.error.required': 'Enter a mobile number, or switch notifications off.',
  'notifications.error.invalid_time': 'Use a time like 19:00.',
  'notifications.error.unknown_loading_place':
    'One of the selected loading places no longer exists.',
  // Verzendlog in the update log (docs/logistics.md §12.5)
  'staging.sms.title': 'Notification',
  'staging.sms.chars': '{{count}} characters',
  'staging.sms.shortened': 'shortened to stay within one message',
  'staging.sms.status.PENDING': 'waiting for the bundling window',
  'staging.sms.status.SENDING': 'sending',
  'staging.sms.status.SENT': 'sent',
  'staging.sms.status.DRY_RUN': 'not sent — dry run is on',
  'staging.sms.status.NO_RECIPIENTS': 'no recipients',
  'staging.sms.status.NOT_CONFIGURED': 'not sent — Spryng is not configured',
  'staging.sms.status.FAILED': 'failed',
  'staging.sms.rstatus.SENT': 'sent',
  'staging.sms.rstatus.DELIVERED': 'delivered',
  'staging.sms.rstatus.READ': 'read',
  'staging.sms.rstatus.SENDING': 'being sent',
  'staging.sms.rstatus.FAILED': 'failed',
  'staging.sms.rstatus.NOT_CONFIGURED': 'not sent',
  'staging.sms.rstatus.DRY_RUN': 'dry run',
  'staging.sms.rstatus.SKIPPED': 'skipped',
  'staging.sms.skip.sms_disabled': 'notifications are off',
  'staging.sms.skip.no_mobile': 'no number',
  'staging.sms.skip.event_not_wanted': 'this event is not ticked',
  'staging.sms.skip.place_not_wanted': 'other loading places chosen',
  'staging.sms.skip.quiet_hours': 'quiet hours',
  'staging.sms.skip.not_allowlisted': 'number is not on the allow-list',
  'staging.sms.skip.fallback_expired': 'too late for a text',
  'staging.sms.skip.unknown': 'unknown',
  'staging.sms.channel.WHATSAPP': 'WhatsApp',
  'staging.sms.channel.SMS': 'SMS',
  'staging.sms.fallback': '{{from}} failed → {{to}} {{status}}',

  // The delivery date (docs/coordination.md §8, bouwlijst #7)
  'deliveryState.NIEUW': 'New',
  'deliveryState.IN_BEHANDELING': 'In progress',
  'deliveryState.BEVESTIGD': 'Confirmed',
  'deliveryState.AFGEWEZEN': 'Rejected',
  'placementSource.TRIP': 'trip',
  'placementSource.CONFIRMED': 'confirmed delivery date',
  'placementSource.DESIRED': 'desired delivery date',
  'delivery.title': 'Delivery date',
  'delivery.meta': 'negotiation with Alfen',
  'delivery.propose': 'Propose',
  'delivery.newProposal': 'New proposal',
  'delivery.reasonShort': 'reason',
  'delivery.placeholder': 'placeholder',
  'delivery.placeholderHelp':
    'In the source, 31 December is a placeholder, not a day. VHT counts it as not known yet.',
  'delivery.desired': 'Desired',
  'delivery.byCustomer': 'customer',
  'delivery.proposalVht': 'Proposal VHT',
  'delivery.proposalAlfen': 'Proposal Alfen',
  'delivery.viaPortal': 'via the portal',
  'delivery.waiting_one': 'waiting — open for {{count}} day',
  'delivery.waiting_other': 'waiting — open for {{count}} days',
  'delivery.confirmed': 'Confirmed',
  'delivery.notYet': 'not yet',
  'delivery.critical':
    'Critical: production finishes on {{date}}, less than a week before the confirmed delivery date.',
  'delivery.alfenHolds': 'At Alfen',
  'delivery.isah': 'Isah',
  'delivery.alfenNotYet': 'not confirmed yet',
  'delivery.differs': 'differs',
  'delivery.calc.confirmed': 'confirmed',
  'delivery.calc.vht': 'proposal VHT',
  'delivery.calc.alfen': 'proposal Alfen',
  'delivery.calc.location': 'location',
  'delivery.calc.production': 'production',
  'delivery.derivedState': 'Derived state',
  'delivery.ruleOf': 'rule {{rule}} of 6',
  'delivery.rule.1': 'confirmed, and no proposal differs',
  'delivery.rule.2': 'the proposals of VHT and Alfen are equal',
  'delivery.rule.3': 'on location VHT and out of production — no handshake needed',
  'delivery.rule.4': 'proposal VHT filled in',
  'delivery.rule.5': 'only Alfen names a date',
  'delivery.rule.6': 'nobody named a date yet',
  'delivery.derivedHelp':
    'This state is recalculated from the dates above every time. It is stored nowhere, so it cannot be set by hand.',
  'delivery.thresholds': 'Production status thresholds',
  'delivery.threshold.41': 'Request to the grid operator may go out',
  'delivery.threshold.64': 'Alfen can no longer reject a confirmed date',
  'delivery.threshold.71': 'On location VHT ⇒ confirmed without a handshake',
  'delivery.thresholdNow': 'Now — {{what}}',
  'delivery.can.new': 'No proposal yet. Alfen answers a proposal from VHT.',
  'delivery.can.inProgress':
    'Alfen can confirm, or reject with a date of its own. That answer comes through the Alfen portal, which is still to be built.',
  'delivery.can.rejected':
    'Alfen rejected the proposal with a date of its own. Make a new proposal — the same date as Alfen’s confirms it.',
  'delivery.can.noHandshake':
    'Confirmed without a handshake: the station is on location VHT and out of production.',
  'delivery.can.confirmedOpen':
    'Alfen can still reject the confirmed date, until production status reaches 64.',
  'delivery.can.confirmedLocked':
    'The date is fixed: Alfen can no longer reject it, production status is {{prod}}.',
  'delivery.placementFrom': 'Expected placement {{date}}, from the {{source}}.',
  'delivery.placementOnHold': 'No expected placement: the dossier is on hold.',
  'delivery.placementNone': 'No expected placement yet.',
  'delivery.placementSkipped': '31 December counts as not known yet.',
  'delivery.casemapDue': 'Case folder from {{party}} by {{date}} ({{weeks}} weeks ahead).',
  'delivery.casemapLate':
    'Case folder from {{party}} is missing — the deadline passed on {{date}} ({{weeks}} weeks ahead).',
  'delivery.casemapNoDate': 'Case folder from {{party}}: {{weeks}} weeks before placement.',
  'delivery.casemapDelivered': 'Case folder from {{party}} is in.',
  'delivery.proposeHelp': 'Desired by the customer: {{date}}.',
  'delivery.proposalDate': 'Proposed delivery date',
  'delivery.takeAlfen': 'Take Alfen’s date — {{date}}',
  'delivery.remark': 'Remark VHT',
  'delivery.needsDate': 'A proposal needs a date.',
  'delivery.otherNeedsRemark': '"Other reason" needs an explanation in the remark.',
  'delivery.withdraw': 'Withdraw proposal',
  'delivery.timelineProposed': 'Delivery date proposed: {{date}}',
  'delivery.timelineWithdrawn': 'Delivery date proposal withdrawn',
  'delivery.timelineAlfenConfirmed': 'Alfen confirmed the delivery date: {{date}}',
  'delivery.timelineAlfenRejected': 'Alfen rejected and proposed {{date}}',

  'login.portalNote':
    'For Van Hooft partners: your password and the code from your authenticator app. Forgot your password or lost your phone? Call us.',
  'login.portalUnavailable': 'Signing in to the portal is not possible right now.',
  'nav.groundwork': 'Groundwork reports',
  'nav.additionalWork': 'Extra work',
  'nav.relations': 'Relations',
  'partyKind.OPDRACHTGEVER': 'Client',
  'partyKind.NETBEHEERDER': 'Grid operator',
  'partyKind.KETENPARTIJ': 'Chain partner',
  'portalModule.GROUNDWORK_REPORT': 'Groundwork photo report',
  'portalModuleHint.GROUNDWORK_REPORT':
    'Submit photos of groundwork; the PDF is mailed to Alfen Transport.',
  'portalModule.DELIVERY_PLANNING': 'Planning overview',
  'portalModuleHint.DELIVERY_PLANNING':
    'Confirm or reject the delivery dates Van Hooft proposes. Only a client answers.',
  'portalAccess.invited': 'Invited',
  'portalAccess.invitation_expired': 'Invitation expired',
  'portalAccess.active': 'Active',
  'portalAccess.locked': 'Locked',
  'portalAccess.inactive': 'Inactive',

  'portal.brand': 'Portal',
  'portal.alfen.product': 'Logistics',
  'portal.alfen.loginTitle': 'Sign in',
  'portal.alfen.loginLede':
    'With your email address and password. The portal then asks for the code from your authenticator app.',
  'portal.alfen.help':
    'No account yet, forgot your password or lost your phone? Call Van Hooft Transport.',
  'portal.alfen.operatedBy': 'Operated by Van Hooft Transport',
  'portal.nav.label': 'Portal',
  'portal.nav.groundwork': 'Groundwork',
  'portal.nav.account': 'My account',
  'portal.shell.vhtGroup': 'Van Hooft Transport',
  'portal.shell.contactPlanning': 'Contact planning',
  'portal.shell.menu': 'Menu',
  'portal.shell.closeMenu': 'Close menu',
  'portal.shell.language': 'Language',
  'portal.shell.dutch': 'Nederlands',
  'portal.shell.english': 'English',
  'portal.busy': 'Working…',
  'portal.back': 'Back',
  'portal.copy': 'Copy',
  'portal.copied': 'Copied',
  'portal.toLogin': 'To sign in',
  'portal.help': 'Need help? Call Van Hooft Transport.',
  'portal.code.lede':
    'Password accepted. Open your authenticator app and enter the code for {{email}}.',
  'portal.code.label': 'Code from the app',
  'portal.code.confirm': 'Sign in',
  'portal.code.link': 'Confirm',
  'portal.recovery.use': 'Lost your phone? Use a recovery code',
  'portal.recovery.lede':
    'Enter one of your recovery codes. You then link a new phone first; the code is used up.',
  'portal.recovery.label': 'Recovery code',
  'portal.recovery.submit': 'Sign in with recovery code',
  'portal.recovery.backToCode': 'Use the code from the app after all',
  'portal.app.step1':
    'Install an authenticator app on your phone, for example Microsoft Authenticator or Google Authenticator.',
  'portal.app.step2':
    'In the app, choose "add account" and scan the QR code (or tap the button on your phone).',
  'portal.app.step3': 'Enter the six-digit code the app shows below.',
  'portal.app.qrLabel': 'QR code to link the authenticator app',
  'portal.app.openOnPhone': 'Open in the authenticator app',
  'portal.app.manualKey': 'Scanning does not work? Type this key',
  'portal.codes.title': 'Recovery codes',
  'portal.codes.onceTitle': 'Keep these codes now.',
  'portal.codes.onceBody':
    'They are shown only once. A recovery code gets you in when your phone is lost; each code works once.',
  'portal.codes.download': 'Download',
  'portal.codes.print': 'Print',
  'portal.codes.kept': 'I have kept the recovery codes',
  'portal.codes.fileHeader': 'Recovery codes — Van Hooft Transport portal',
  'portal.codes.fileFoot':
    'Each code works once. Keep this file somewhere safe, not on the same phone as your authenticator app.',
  'portal.password.current': 'Current password',
  'portal.password.new': 'New password',
  'portal.password.repeat': 'Again',
  'portal.password.show': 'Show password',
  'portal.password.hide': 'Hide password',
  'portal.password.ruleLength': 'At least {{min}} characters',
  'portal.password.ruleNotEmail': 'Not your e-mail address',
  'portal.password.ruleSame': 'The same twice',
  'portal.password.rule.too_short': 'The password is too short.',
  'portal.password.rule.too_long': 'The password is too long (at most 128 characters).',
  'portal.password.rule.same_as_email': 'Choose something other than your e-mail address.',
  'portal.password.rule.same_as_current': 'The new password is the same as the current one.',
  'portal.error.generic': 'Something went wrong. Please try again.',
  'portal.error.loginFailed': 'E-mail address or password is not right.',
  'portal.error.locked':
    'Too many wrong attempts. The account is locked for 15 minutes; try again after that.',
  'portal.error.codeWrong':
    'This code is not right. Wait for the next code in the app and try again.',
  'portal.error.signInAgain':
    'That took too long. Start again with your e-mail address and password.',
  'portal.error.linkInvalid':
    'This link has expired or was used already. Ask Van Hooft for a new invitation.',
  'portal.error.passwordWrong': 'The password is not right.',
  'portal.error.unavailable': 'Signing in to the portal is not possible right now.',
  'portal.error.network': 'No connection. Check your internet and try again.',
  'portal.error.tooMany': 'Too many attempts in a row. Wait a moment and try again.',
  'portal.activate.title': 'Set up your account',
  'portal.activate.lede': 'Welcome {{name}}. You are setting up portal access for {{party}}.',
  'portal.activate.progress': 'Progress',
  'portal.activate.stepPassword': 'Password',
  'portal.activate.stepApp': 'Link the app',
  'portal.activate.stepCodes': 'Recovery codes',
  'portal.activate.invalidTitle': 'This link does not work.',
  'portal.activate.passwordLede':
    'Choose a password for {{email}}. Length matters more than symbols: a sentence works well.',
  'portal.activate.next': 'Next',
  'portal.activate.firstCode': 'Code from the app',
  'portal.activate.toPortal': 'To the portal',
  'portal.phone.title': 'Link a new phone',
  'portal.phone.lede':
    'Move the portal to another phone. The old phone keeps working until the new one has given its first code.',
  'portal.phone.ledeRelink':
    'You signed in with a recovery code. Link a phone first; then the portal works again.',
  'portal.phone.relinkNotice_one':
    'You have {{count}} recovery code left. Create new ones under My account after linking.',
  'portal.phone.relinkNotice_other':
    'You have {{count}} recovery codes left. Create new ones under My account after linking.',
  'portal.phone.start': 'Link phone',
  'portal.phone.firstCode': 'Code from the app on the new phone',
  'portal.account.title': 'My account',
  'portal.account.passwordTitle': 'Password',
  'portal.account.passwordSince': 'Chosen on {{when}}',
  'portal.account.passwordChanged': 'Password changed. Other sessions have been signed out.',
  'portal.account.passwordSubmit': 'Change password',
  'portal.account.phoneTitle': 'Authenticator app',
  'portal.account.phoneBody': 'New phone? Link it here while you still have the old one.',
  'portal.account.phoneAction': 'Link another phone',
  'portal.account.codesTitle': 'Recovery codes',
  'portal.account.codesLeft_one': '{{count}} code left',
  'portal.account.codesLeft_other': '{{count}} codes left',
  'portal.account.codesBody': 'Create eight new codes. The old codes stop working.',
  'portal.account.codesAction': 'New recovery codes',
  'portal.account.codesDone': 'Done',

  'groundwork.title': 'Record groundwork',
  'groundwork.lede':
    'Fill in the order and add the photos. Van Hooft turns them into a PDF with our logo, the details and all photos, and mails it to Alfen Transport.',
  'groundwork.required': 'required',
  'groundwork.optional': 'optional',
  'groundwork.side': 'Preview and sending',
  'groundwork.order.title': 'Order',
  'groundwork.order.number': 'VHT order number',
  'groundwork.order.numberPlaceholder': 'e.g. 26-04127',
  'groundwork.order.executedOn': 'Date carried out',
  'groundwork.order.address': 'Address',
  'groundwork.order.addressPlaceholder': 'Street and number, postcode and town',
  'groundwork.order.remarks': 'Remarks',
  'groundwork.order.remarksPlaceholder':
    'E.g. notes on the subsoil, deviations or arrangements on site',
  'groundwork.photos.title': 'Photos',
  'groundwork.photos.min': 'at least {{min}}',
  'groundwork.photos.count_one': '{{count}} photo',
  'groundwork.photos.count_other': '{{count}} photos',
  'groundwork.photos.needed': '{{count}} more needed',
  'groundwork.photos.drop': 'Drag photos here',
  'groundwork.photos.orChoose': 'or click to choose them — pasting works too',
  'groundwork.photos.hint': 'JPG or PNG · resized for the mail',
  'groundwork.photos.processing': 'Processing photos: {{at}} of {{of}}',
  'groundwork.photos.unreadable':
    'Did not work: {{files}}. Use JPG or PNG; export iPhone photos (HEIC) as JPG first, or upload from the phone itself.',
  'groundwork.photos.tooMany': 'at most {{max}} photos',
  'groundwork.photos.added': 'Added photos',
  'groundwork.photos.alt': 'Photo {{n}}: {{name}}',
  'groundwork.photos.remove': 'Remove photo {{n}}',
  'groundwork.error.orderNumber': 'Enter the order number.',
  'groundwork.error.executedOn': 'Choose the date it was carried out.',
  'groundwork.error.future': 'The date carried out cannot be in the future.',
  'groundwork.error.address': 'Enter the address.',
  'groundwork.error.photosNone': 'Add at least {{min}} photos.',
  'groundwork.error.photosShort_one': 'Add {{count}} more photo; at least {{min}} are required.',
  'groundwork.error.photosShort_other': 'Add {{count}} more photos; at least {{min}} are required.',
  'groundwork.error.tooLarge':
    'The report is too large to send. Remove photos or send it in two parts.',
  'groundwork.preview.label': 'Preview of the first page of the PDF',
  'groundwork.preview.titlePlaceholder': 'Groundwork · order number · date',
  'groundwork.preview.fillIn': 'to fill in',
  'groundwork.preview.choose': 'to choose',
  'groundwork.preview.photosHere': 'The photos go here',
  'groundwork.preview.meta_one': 'Preview page 1 · A4 · {{count}} page · approx. {{size}}',
  'groundwork.preview.meta_other': 'Preview page 1 · A4 · {{count}} pages · approx. {{size}}',
  'groundwork.send.title': 'Send',
  'groundwork.send.to': 'To',
  'groundwork.send.subject': 'Subject',
  'groundwork.send.attachment': 'Attachment',
  'groundwork.send.replyTo': 'Reply to',
  'groundwork.send.noRecipient': 'no recipient configured',
  'groundwork.send.subjectPlaceholder': '+ order number',
  'groundwork.send.fileNamePlaceholder': 'Grondwerk + order number + date.pdf',
  'groundwork.send.tooBig':
    'The report will be approx. {{size}}. Many mail servers refuse attachments over 15 MB; remove photos or send in two parts.',
  'groundwork.send.notLive':
    'This environment does not really send mail. The report and the PDF are made all the same.',
  'groundwork.send.button': 'Send to Alfen',
  'groundwork.send.uploading': 'Sending photos… {{pct}}%',
  'groundwork.send.making': 'Making the PDF…',
  'groundwork.status.QUEUED': 'Being sent',
  'groundwork.status.SENT': 'Sent',
  'groundwork.status.FAILED': 'Failed',
  'groundwork.status.NOT_SENT': 'Not sent',
  'groundwork.result.sent': '{{file}} was sent to {{to}} at {{time}}.',
  'groundwork.result.queued':
    'The PDF is ready and goes to {{to}} within a few seconds. Nothing for you to do.',
  'groundwork.result.failed':
    'The mail to {{to}} did not arrive. Van Hooft sees this too. Open the PDF and mail it yourself, or call us.',
  'groundwork.result.notSent.dry_run': 'Dry run: this environment sends no mail.',
  'groundwork.result.notSent.no_recipient': 'This environment has no recipient configured.',
  'groundwork.result.notSent.mail_not_configured': 'Mail is not set up in this environment.',
  'groundwork.result.openPdf': 'View PDF',
  'groundwork.result.new': 'New report',
  'groundwork.history.title': 'Sent before',
  'groundwork.history.empty': 'No reports sent yet',
  'groundwork.history.emptyHint':
    'What you send appears here — with the PDF and whether the mail arrived.',
  'groundwork.history.colSent': 'Submitted',
  'groundwork.history.colOrder': 'Order',
  'groundwork.history.colAddress': 'Address',
  'groundwork.history.colExecuted': 'Carried out',
  'groundwork.history.colPhotos': 'Photos',
  'groundwork.history.colMail': 'Mail',

  'additionalWork.title': 'Notice of extra work',
  'additionalWork.subtitle':
    'After an inspection: which extra work Alfen and the grid operator can expect, as a letter',
  'additionalWork.reset': 'Clear form',
  'additionalWork.required': 'required',
  'additionalWork.optional': 'optional',
  'additionalWork.side': 'Preview and download',
  'additionalWork.step.form': 'Inspection form and checklist',
  'additionalWork.step.details': 'Details',
  'additionalWork.step.posts': 'Expected extra work',
  'additionalWork.step.finish': 'Finish',
  'additionalWork.form.drop': 'Drop the inspection form here',
  'additionalWork.form.choose': 'Choose PDF',
  'additionalWork.form.replace': 'Other file',
  'additionalWork.form.hint':
    'Or choose a PDF. Its details are copied over, and the form goes behind the letter as an attachment.',
  'additionalWork.form.reading': 'Reading…',
  'additionalWork.form.pages_one': '{{count}} page · click a page to enlarge it',
  'additionalWork.form.pages_other': '{{count}} pages · click a page to enlarge it',
  'additionalWork.form.notInspectionForm_one':
    '{{count}} page · this does not look like a Van Hooft inspection form; fill in the details yourself',
  'additionalWork.form.notInspectionForm_other':
    '{{count}} pages · this does not look like a Van Hooft inspection form; fill in the details yourself',
  'additionalWork.form.found_one': '{{count}} field copied',
  'additionalWork.form.found_other': '{{count}} fields copied',
  'additionalWork.form.foundNone': 'No details found',
  'additionalWork.form.notPdf': 'This is not a PDF. Choose the inspection form as a PDF file.',
  'additionalWork.form.tooLarge': 'This file is larger than {{size}}. Choose a smaller PDF.',
  'additionalWork.form.unreadable':
    'This file could not be read. Is it a protected or damaged PDF? Choose another file.',
  'additionalWork.form.libFailed':
    'The PDF components could not be loaded. Reload the page and try again.',
  'additionalWork.form.remove': 'Remove inspection form',
  'additionalWork.form.pagesLabel': 'Pages of the inspection form',
  'additionalWork.form.enlarge': 'Enlarge page {{n}}',
  'additionalWork.form.viewer': 'Inspection form · page {{n}} of {{count}}',
  'additionalWork.form.prev': 'Previous',
  'additionalWork.form.next': 'Next',
  'additionalWork.form.fromForm': 'from inspection',
  'additionalWork.form.keptName': 'Inspection form for order {{order}}',
  'additionalWork.form.kept_one': '{{count}} page · from version {{version}} · click to enlarge',
  'additionalWork.form.kept_other':
    '{{count}} pages · from version {{version}} · click a page to enlarge',
  'additionalWork.revision.title': 'Version {{next}} of order {{order}}',
  'additionalWork.revision.titleKept': 'Version {{version}} of order {{order}}',
  'additionalWork.revision.body':
    'You are changing version {{version}} of {{date}}. What you mail or download now becomes version {{next}} and replaces version {{version}}. What changed is shaded in the letter.',
  'additionalWork.revision.kept.mailed':
    'Mailed on {{date}}. Change anything, and what you mail or download becomes version {{next}}.',
  'additionalWork.revision.kept.downloaded':
    'Downloaded on {{date}}. Change anything, and what you mail or download becomes version {{next}}.',
  'additionalWork.revision.kept.notMailed':
    'Not sent ({{date}}). Mail it again, and it becomes version {{next}}.',
  'additionalWork.revision.mailAsIs': 'Mail it unchanged, and it stays version {{version}}.',
  'additionalWork.revision.new': 'New letter',
  'additionalWork.revision.opening': 'Opening the letter…',
  'additionalWork.revision.openFailed': 'The letter could not be opened. {{reason}}',
  'additionalWork.field.orderNumber': 'Order number',
  'additionalWork.field.documentDate': 'Letter date',
  'additionalWork.field.address': 'Work address',
  'additionalWork.field.projectName': 'Project name',
  'additionalWork.field.inspectionDate': 'Inspection date',
  'additionalWork.field.organisation': 'Recipient organisation',
  'additionalWork.field.attention': 'For the attention of',
  'additionalWork.field.attentionHelp': 'Also the salutation: “Beste …”',
  'additionalWork.field.extraExplanation': 'Further explanation',
  'additionalWork.field.extraExplanationPlaceholder':
    'For example: the municipality decides with the permit whether a traffic controller is needed.',
  'additionalWork.field.author': 'Written by',
  'additionalWork.field.authorRole': 'Position',
  'additionalWork.field.attach': 'Put the inspection form behind the letter as an attachment',
  'additionalWork.field.contacts': 'Contacts at the grid operator',
  'additionalWork.field.contactsHelp':
    'From the checklist stationsmap, or fill them in. They go into the letter and are kept with it. The first contact gets the letter when it is mailed.',
  'additionalWork.field.contact1': 'First contact',
  'additionalWork.field.contact2': 'Second contact',
  'additionalWork.field.contact1Name': 'Name',
  'additionalWork.field.contact1Phone': 'Phone',
  'additionalWork.field.contact1Email': 'E-mail',
  'additionalWork.field.contact2Name': 'Name',
  'additionalWork.field.contact2Phone': 'Phone',
  'additionalWork.field.contact2Email': 'E-mail',
  'additionalWork.posts.lede': 'Only ticked items go into the letter.',
  'additionalWork.posts.none': 'No items ticked',
  'additionalWork.posts.summary_one': '{{count}} item · {{amount}}',
  'additionalWork.posts.summary_other': '{{count}} items · {{amount}}',
  'additionalWork.posts.plusTariff': '{{amount}} + rate list',
  'additionalWork.posts.tariff': 'According to the Alfen/grid operator rate list',
  'additionalWork.posts.remarks': 'Remarks from the inspection',
  'additionalWork.posts.other': 'Other',
  'additionalWork.posts.otherSub': 'To be described',
  'additionalWork.posts.standard': 'Standard {{amount}}',
  'additionalWork.posts.from': 'From (euro)',
  'additionalWork.posts.to': 'To (euro)',
  'additionalWork.posts.range': 'Range {{name}}',
  'additionalWork.posts.description': 'Description',
  'additionalWork.posts.descriptionPlaceholder':
    'For example: an extra transport movement or waiting hours',
  'additionalWork.posts.note': 'Explanation',
  'additionalWork.posts.notePlaceholder': 'Goes under the item in the letter',
  'additionalWork.posts.notePlaceholderChips': 'In addition to the chosen work',
  'additionalWork.posts.remove': 'Remove item',
  'additionalWork.posts.addOther': 'Another other item',
  'additionalWork.post.traffic.name': 'Traffic measures',
  'additionalWork.post.traffic.variants': 'Kind of traffic measure',
  'additionalWork.post.traffic.variantHelp':
    'Ranges based on 15 invoices from BUKO Infrasupport (Sep 2026), plus 30%. Municipal fees and parking charges may come on top.',
  'additionalWork.post.traffic.variant.simple': 'Simple',
  'additionalWork.post.traffic.variant.simple.sub': 'parking ban or barrier, one placement',
  'additionalWork.post.traffic.variant.extended': 'Extended',
  'additionalWork.post.traffic.variant.extended.sub':
    'temporary measure with signage plan, several days',
  'additionalWork.post.road_plates.name': 'Road plates',
  'additionalWork.post.fencing.name': 'Construction fencing',
  'additionalWork.post.fencing.sub': 'the amount depends on the rental period',
  'additionalWork.post.fencing.notePlaceholder':
    'For example: expected rental period about 2 weeks',
  'additionalWork.post.groundwork.name': 'Groundwork',
  'additionalWork.post.clear_shrubs.name': 'Remove shrubs',
  'additionalWork.post.heavier_crane.name': 'Heavier crane',
  'additionalWork.post.surveyor.name': 'Extra surveyor time',
  'additionalWork.post.pre_inspection.name': 'Pre-inspection at the grid operator’s request',
  'additionalWork.post.pre_inspection.sub': 'when the grid operator asks for an extra inspection',
  'additionalWork.chip.traffic_controllers': 'Traffic controllers',
  'additionalWork.chip.road_closure': 'Road closure',
  'additionalWork.chip.crash_cushion': 'Crash cushion',
  'additionalWork.chip.clear_site': 'Clear the site',
  'additionalWork.chip.remove_soil': 'Remove soil',
  'additionalWork.chip.clean_fill': 'Fill with clean soil',
  'additionalWork.chip.level': 'Level',
  'additionalWork.preview.label': 'PDF preview',
  'additionalWork.preview.building': 'Updating…',
  'additionalWork.preview.failed': 'The preview could not be made',
  'additionalWork.preview.pages_one': '{{count}} page',
  'additionalWork.preview.pages_other': '{{count}} pages',
  'additionalWork.preview.page': 'Letter, page {{n}}',
  'additionalWork.preview.waiting': 'The letter appears here.',
  'additionalWork.preview.attachment_one': 'Plus {{count}} page of inspection form as attachment',
  'additionalWork.preview.attachment_other':
    'Plus {{count}} pages of inspection form as attachment',
  'additionalWork.issue.title': 'Still needed for the download',
  'additionalWork.issue.orderNumber': 'Fill in the order number.',
  'additionalWork.issue.address': 'Fill in the address of the work.',
  'additionalWork.issue.attention': 'Fill in who the letter is for.',
  'additionalWork.issue.contactEmail': 'An e-mail address of a contact is not valid.',
  'additionalWork.issue.author': 'Fill in a name under “Written by”.',
  'additionalWork.issue.noPosts': 'Tick at least one item of extra work.',
  'additionalWork.issue.tooMany': 'At most {{max}} items in one letter.',
  'additionalWork.issue.description': 'Give a description.',
  'additionalWork.issue.amount': 'Fill in an amount.',
  'additionalWork.issue.reversed': 'The upper amount is below the lower one.',
  'additionalWork.issue.post': '{{name}}: {{message}}',
  'additionalWork.list.drop': 'Drag the checklist stationsmap here',
  'additionalWork.list.hint': 'Optional. The checklist stationsmap, as an Excel file.',
  'additionalWork.list.choose': 'Choose Excel',
  'additionalWork.list.replace': 'Other file',
  'additionalWork.list.reading': 'Reading…',
  'additionalWork.list.notXlsx': 'This is not an Excel file. Choose the checklist as .xlsx.',
  'additionalWork.list.tooLarge': 'This file is larger than {{size}}.',
  'additionalWork.list.unreadable':
    'VHT only reads Liander’s checklist stationsmap. Fill in the posts and contacts yourself.',
  'additionalWork.list.failed': 'The checklist could not be read. Please try again.',
  'additionalWork.list.station': 'Case {{caseNumber}} · station {{stationNumber}} {{stationName}}',
  'additionalWork.list.status': 'Status of the checklist: {{status}}',
  'additionalWork.list.group.post': 'Extra work according to the checklist',
  'additionalWork.list.group.attention': 'Read the remark',
  'additionalWork.list.group.none': 'No extra work',
  'additionalWork.list.row': 'row {{row}}',
  'additionalWork.list.empty': 'not filled in',
  'additionalWork.list.quiet_one': '{{count}} question about the site without remarks.',
  'additionalWork.list.quiet_other': '{{count}} questions about the site without remarks.',
  'additionalWork.list.contacts': 'Contacts taken over: {{names}}.',
  'additionalWork.list.remove': 'Remove checklist',
  'additionalWork.list.fromList': 'from checklist',
  'additionalWork.list.why': 'Checklist: {{reasons}}',
  'additionalWork.list.reason': 'row {{row}}, “{{answer}}”',
  'additionalWork.list.topic.extra_inspection': 'Extra inspection',
  'additionalWork.list.topic.excavation_safety': 'Fencing the foundation pit',
  'additionalWork.list.topic.excavated_soil': 'Excavated soil',
  'additionalWork.list.topic.after_placement': 'After placement',
  'additionalWork.list.topic.obstacles': 'Free of obstacles',
  'additionalWork.list.topic.boundary': 'Clear of the boundary',
  'additionalWork.list.topic.access': 'Freely accessible',
  'additionalWork.list.topic.heavy_transport': 'Reachable for heavy transport',
  'additionalWork.list.topic.trees': 'Outside the vulnerable tree zone',
  'additionalWork.list.topic.traffic': 'No traffic measures needed',
  'additionalWork.complete': 'Complete. The letter can be downloaded.',
  'additionalWork.download.button': 'Download PDF',
  'additionalWork.download.making': 'Making the PDF…',
  'additionalWork.download.done': 'Downloaded: {{file}}. The letter is in the list below.',
  'additionalWork.download.failed': 'The PDF could not be made. {{reason}}',
  'additionalWork.subject.label': 'Subject for the email',
  'additionalWork.subject.copy': 'Copy',
  'additionalWork.subject.copied': 'Copied',
  'additionalWork.mail.button': 'Mail the letter',
  'additionalWork.mail.buttonVersion': 'Mail version {{version}}',
  'additionalWork.mail.version': 'Version',
  'additionalWork.mail.versionValue': '{{version}}, replaces version {{replaces}}',
  'additionalWork.mail.versionAsIs': '{{version}}, as downloaded',
  'additionalWork.mail.stale':
    'A newer version of this letter has been mailed or downloaded in the meantime. Open that one in the list below.',
  'additionalWork.mail.sentButton': 'Mailed',
  'additionalWork.mail.confirmTitle': 'Mail the letter',
  'additionalWork.mail.send': 'Send',
  'additionalWork.mail.sending': 'Sending…',
  'additionalWork.mail.to': 'To',
  'additionalWork.mail.subject': 'Subject',
  'additionalWork.mail.attachment': 'Attachment',
  'additionalWork.mail.pages_one': '{{count}} page',
  'additionalWork.mail.pages_other': '{{count}} pages',
  'additionalWork.mail.replyTo': 'Reply to',
  'additionalWork.mail.noRecipient': 'no recipient set',
  'additionalWork.mail.bcc': 'Bcc',
  'additionalWork.mail.final':
    'The letter goes out on behalf of Van Hooft and cannot be recalled. VHT keeps it, with the state of the mail.',
  'additionalWork.mail.failed': 'Not sent. {{reason}}',
  'additionalWork.mail.noRecipientNote':
    'No recipient is set in this environment: the letter is kept, but not mailed.',
  'additionalWork.mail.dryRun':
    'Dry run: the letter would go to {{to}}, but no mail is really sent.',
  'additionalWork.mail.goesTo': 'Goes to {{to}}.',
  'additionalWork.mail.dryRunDirect':
    'Dry run: the letter would go to {{to}}, with a copy (bcc) to {{bcc}}, but no mail is really sent.',
  'additionalWork.mail.goesToDirect': 'Goes to {{to}}, with a copy (bcc) to {{bcc}}.',
  'additionalWork.mail.repliesTo': 'A reply comes in at {{replyTo}}.',
  'additionalWork.mail.directHint':
    'With an e-mail address for the first contact, they get it themselves.',
  'additionalWork.mail.redirect':
    'In this environment every mail goes to {{to}}, with who it was meant for at the top.',
  'additionalWork.status.QUEUED': 'Sending',
  'additionalWork.status.SENT': 'Sent',
  'additionalWork.status.FAILED': 'Failed',
  'additionalWork.status.NOT_SENT': 'Not sent',
  'additionalWork.status.DOWNLOADED': 'Downloaded',
  'additionalWork.result.sent': 'Mailed to {{to}} at {{time}}.',
  'additionalWork.result.sentOn': 'Mailed to {{to}} on {{date}}.',
  'additionalWork.result.queued': 'The letter is on its way to {{to}}.',
  'additionalWork.result.failed':
    'The mail to {{to}} failed after several tries. The letter is kept; tell an administrator.',
  'additionalWork.result.notSent.dry_run': 'Mail is in dry-run mode in this environment.',
  'additionalWork.result.notSent.no_recipient': 'No recipient is set in this environment.',
  'additionalWork.result.notSent.mail_not_configured': 'Mail is not set up in this environment.',
  'additionalWork.result.downloaded': 'Version {{version}}, on {{date}}. It is in the list below.',
  'additionalWork.history.title': 'Sent or downloaded',
  'additionalWork.history.empty': 'Nothing sent or downloaded yet',
  'additionalWork.history.emptyHint':
    'A letter you mail or download appears here, with the state of its mail. Click a row to open the letter again and change it.',
  'additionalWork.history.hint':
    'Click a letter to change it and mail or download it as a new version.',
  'additionalWork.history.search': 'Search by order number or address',
  'additionalWork.history.noMatch': 'No letter found for “{{q}}”',
  'additionalWork.history.colVersion': 'Version',
  'additionalWork.history.earlier_one': '{{count}} earlier',
  'additionalWork.history.earlier_other': '{{count}} earlier',
  'additionalWork.history.pdf': 'PDF of version {{version}}',
  'additionalWork.history.colSent': 'Date',
  'additionalWork.history.colOrder': 'Order',
  'additionalWork.history.colAddress': 'Address',
  'additionalWork.history.colAmount': 'Estimate',
  'additionalWork.history.colMail': 'State',
  'additionalWork.history.colPdf': 'PDF',

  'groundworkAdmin.title': 'Groundwork reports',
  'groundworkAdmin.mailLive': 'Reports are mailed from this environment to {{to}}.',
  'groundworkAdmin.mailDry': 'Dry run: reports would go to {{to}}, but no mail is really sent.',
  'groundworkAdmin.mailNone':
    'No recipient configured (GROUNDWORK_MAIL_TO): reports are kept, not mailed.',
  'groundworkAdmin.searchPlaceholder': 'Search by order, address, party or person…',
  'groundworkAdmin.colParty': 'Party',
  'groundworkAdmin.submittedBy': 'Submitted by',
  'groundworkAdmin.pdf': 'PDF',
  'groundworkAdmin.pdfMeta': '{{photos}} photos · {{pages}} p. · {{kb}} kB',
  'groundworkAdmin.note': 'Note',
  'groundworkAdmin.lastError_one': 'Last error (after {{count}} attempt)',
  'groundworkAdmin.lastError_other': 'Last error (after {{count}} attempts)',
  'groundworkAdmin.resend': 'Send again',
  'groundworkAdmin.downloadPdf': 'Download PDF',
  'groundworkAdmin.empty': 'No groundwork reports yet',
  'groundworkAdmin.emptyHint':
    'As soon as a partner sends one through the portal, it appears here.',

  'dataOwners.colKind': 'Kind',
  'dataOwners.colPortal': 'Portal',
  'dataOwners.fieldKind': 'Kind',
  'dataOwners.fieldKindHint': 'Decides what the party sees of the orders in the portal.',
  'dataOwners.backToList': 'Parties',
  'dataOwners.edit': 'Edit',
  'dataOwners.codes.title': 'External codes',
  'dataOwners.codes.add': 'Add code',
  'dataOwners.codes.colSystem': 'System',
  'dataOwners.codes.colCode': 'Code',
  'dataOwners.codes.colOrders': 'Dossiers',
  'dataOwners.codes.empty': 'No external codes yet',
  'dataOwners.codes.emptyHint':
    'Add the code Alfen uses for this party, and its dossiers get linked.',
  'dataOwners.codes.addTitle': 'Add external code',
  'dataOwners.codes.fieldSystem': 'System',
  'dataOwners.codes.fieldCode': 'Code',
  'dataOwners.codes.fieldCodeHint':
    'Dossiers that already came in with this code are linked at once.',
  'dataOwners.codes.taken': 'This code already belongs to another party.',
  'dataOwners.codes.takenBy': '{{code}} {{name}} already has this code.',
  'dataOwners.codes.linked_one': '{{name}} has {{count}} dossier through this code.',
  'dataOwners.codes.linked_other': '{{name}} has {{count}} dossiers through this code.',
  'dataOwners.codes.removeNote':
    'Removing unlinks those dossiers. Their code and name as Alfen sent them stay.',
  'dataOwners.codes.remove': 'Remove code',
  'externalSystem.ALFEN': 'Alfen',
  'externalSystemHint.ALFEN': "Alfen's customer code (DM_CustId), as on an Alfen order.",
  'dataOwners.portal.modulesTitle': 'Portal parts',
  'dataOwners.portal.modulesHint':
    "What this party's portal users may open. Signing in and their own account always work.",
  'dataOwners.portal.usersTitle': 'Portal users',
  'dataOwners.portal.invite': 'Invite',
  'dataOwners.portal.mailNotLive':
    'Invitations are not mailed in this environment. You get the link to pass on yourself.',
  'dataOwners.portal.colName': 'Name',
  'dataOwners.portal.colEmail': 'E-mail address',
  'dataOwners.portal.colState': 'Access',
  'dataOwners.portal.colLastLogin': 'Last signed in',
  'dataOwners.portal.empty': 'Nobody from this party has access yet',
  'dataOwners.portal.emptyHint':
    'Invite someone: they choose a password and link an authenticator app themselves.',
  'dataOwners.portal.inviteTitle': 'Invite a portal user',
  'dataOwners.portal.inviteLede':
    'The invitation is valid for seven days. The person chooses password and app themselves; you never see or know them.',
  'dataOwners.portal.inviteSubmit': 'Invite',
  'dataOwners.portal.invitedAt': 'Invited',
  'dataOwners.portal.linkValidUntil': 'Link valid until',
  'dataOwners.portal.activatedAt': 'Set up',
  'dataOwners.portal.recoveryLeft': 'Recovery codes left',
  'dataOwners.portal.lockedUntil': 'Locked until',
  'dataOwners.portal.reinvite': 'Send a new invitation',
  'dataOwners.portal.unlock': 'Unlock',
  'dataOwners.portal.reset': 'Reset access',
  'dataOwners.portal.resetConfirm':
    'Password, app and recovery codes are dropped, and all sessions end. The person gets a new invitation. For a lost phone without recovery codes, or when in doubt.',
  'dataOwners.portal.resetSubmit': 'Yes, reset access',
  'dataOwners.portal.deactivate': 'Revoke access',
  'dataOwners.portal.activate': 'Give access again',
  'dataOwners.portal.deliveryTitle': 'Invitation',
  'dataOwners.portal.mailed': 'The invitation was mailed to {{email}}.',
  'dataOwners.portal.notMailed.dry_run':
    'Dry run: nothing was mailed. Pass this link on to {{email}} yourself.',
  'dataOwners.portal.notMailed.mail_not_configured':
    'Mail is not set up in this environment. Pass this link on to {{email}} yourself.',
  'dataOwners.portal.notMailed.mail_failed':
    'Mailing failed. Pass this link on to {{email}} yourself.',
  'dataOwners.portal.link': 'Invitation link',
  'dataOwners.portal.linkHint': 'Works once, valid until {{date}}. This link is not shown again.',

  // Opslag — plattegronden (docs/storage.md)
  'nav.storage': 'Storage',
  'nav.floorplans': 'Floor plans',
  'storage.title': 'Floor plans',
  'storage.subtitle': 'Where everything stands on the yard, per storage location.',
  'storage.summary': '{{plans}} floor plans · {{count}} in storage',
  'storage.stationCount_one': '{{count}} in storage',
  'storage.stationCount_other': '{{count}} in storage',
  'storage.nOfM': '{{n}} of {{m}}',
  // The filter on an owner, all over Opslag (owners step 7, handoff `eigenaren.md §4`)
  'storage.ownerFilter': 'Owner',
  'storage.ownerAll': 'All',
  'storage.summaryOwner': '{{plans}} floor plans · {{n}} of {{count}} in storage',
  'storage.ownerCount': '{{count}} of {{owner}}',
  'storage.ownerNone': 'Nothing of {{owner}}',
  'storage.listOwner': '{{n}} of {{m}} · {{owner}}',
  'storage.ownerEmptyTitle': 'Nothing of {{owner}} on {{plan}}',
  'storage.ownerEmptyBody': 'What stands here belongs to another owner: it is dimmed on the map.',
  'storage.ownerShowAll': 'Show all owners',
  'storage.updated': 'Changed {{at}}',
  'storage.notUpdated': 'Not changed yet',
  'storage.noLoadingPlace': 'No loading place linked',
  'storage.noPlansTitle': 'No floor plans yet',
  'storage.noPlansBody': 'An administrator creates a floor plan, with a drawing of the yard.',
  'storage.loadErrorTitle': 'The floor plan could not be loaded',
  'storage.loadErrorBody': 'Check the connection on the yard and try again.',
  'storage.addStation': 'Add',
  'storage.view': 'View',
  'storage.viewMap': 'Map',
  'storage.viewList': 'List',
  'storage.search': 'Search by order number, name or customer',
  'storage.clearSearch': 'Clear search',
  'storage.emptyTitle': 'Nothing on {{plan}} yet',
  'storage.emptyBody':
    'Add what is there. It appears at the start position; then drag it into place.',
  'storage.noMatchTitle': 'Nothing found',
  'storage.noMatchBody':
    'Nothing on {{plan}} with “{{query}}” in the order number, the name or the customer.',
  'storage.noCustomer': 'no customer',
  'storage.planLoading': 'Loading floor plan…',
  'storage.noDrawingTitle': 'No drawing for {{plan}}',
  'storage.noDrawingAdmin':
    'Without a drawing you cannot place anything. Upload an SVG of the yard.',
  'storage.noDrawingBody':
    'Without a drawing you cannot place anything. Ask an administrator to upload one.',
  'storage.zoomIn': 'Zoom in',
  'storage.zoomOut': 'Zoom out',
  'storage.zoomFit': 'Whole yard',
  'storage.selected': 'Selected',
  'storage.portrait': 'Standing',
  'storage.landscape': 'Lying',
  'storage.newHint': '{{code}} is at the start position. Drag it into place on the yard.',
  'storage.customer': 'Customer',
  'storage.owner': 'Owner',
  'storage.adrLabels': 'ADR {{un}} · right labels?',
  'storage.adrOk': 'In order',
  'storage.adrNotOk': 'Not in order',
  'storage.adrState.ok': 'Labels in order',
  'storage.adrState.notOk': 'Labels not in order',
  'storage.adrState.unchecked': 'Labels not checked yet',
  'storage.adrFilter': 'Only ADR not in order ({{count}})',
  'storage.adrAllOk': 'Every battery on {{plan}} has the right labels.',
  'storage.adrFailed': 'The ADR state of {{code}} was not saved. Try again.',
  'storage.legendAdr': 'ADR {{un}}',
  'storage.legendAdrAny': 'ADR',
  'storage.legendAdrNotOk': 'not in order {{count}}',
  'storage.legendAdrUnchecked': 'not yet checked {{count}}',
  'storage.legendAdrOk': '{{count}} in order',
  'storage.legendAdrAllOk': 'all {{count}} in order',
  'storage.lastMoved': 'Last moved',
  'storage.rotate': 'Rotate',
  'storage.edit': 'Edit',
  'storage.history': 'History',
  'storage.moved': '{{code}} moved',
  'storage.rotated': '{{code}} rotated',
  'storage.undo': 'Undo',
  'storage.moveFailed': '{{code}} not moved — no connection. It is back in its place.',
  'storage.moveRefused': '{{code}} not moved. It is back in its place.',
  'storage.rotateFailed': '{{code}} not rotated. Try again.',
  'storage.added': '{{code}} added to {{plan}}',
  'storage.deleted': '{{code}} removed from {{plan}}',
  'storage.export': 'Export to Excel',
  'storage.exported': '{{file}} downloaded · {{count}} on {{plans}} floor plans',
  'storage.exportedOwner': '{{file}} downloaded · {{count}} of {{owner}}',
  'storage.exportFor': 'Export for',
  'storage.exportOwnerHint': '{{count}} in storage',
  'storage.exportAll': 'Everything',
  'storage.exportAllHint': 'With an owner column',
  'storage.exportFailed': 'The export failed. Try again.',
  'storage.mapUnavailable':
    'No map in this environment: the HERE key is not set. The floor plans are listed alongside.',
  'storage.mapNoPoints': 'No floor plan has a place on the map yet.',
  'station.code': 'Order number',
  'station.codeHelp': 'At least 8 characters. Capitals, no spaces.',
  'station.codeName': 'Name or batch',
  'station.codeHelpName': 'As on the battery or in the WMS, e.g. Leticia_253 or 001. No spaces.',
  'station.owner': 'Owner',
  'station.ownerRequired': 'Choose an owner.',
  'station.ownerFirst': 'Choose the owner first: its types then appear here.',
  'station.codeRequired': 'Required.',
  'station.codeMin': 'At least {{min}} characters.',
  'station.type': 'Type',
  'station.typeRequired': 'Choose a type.',
  'station.customer': 'Customer',
  'station.customerSearch': 'Search by code or name',
  'station.customerNone': 'No customer found. Customers come from the parties in VHT.',
  'station.customerClear': 'Clear customer',
  'station.editTitle': 'Edit {{code}}',
  'station.addTitle': 'Add to {{plan}}',
  'station.codeAny': 'Order number or name',
  'station.codeOwnerFirst': 'Choose the owner first.',
  'station.ownerPick': 'Choose an owner',
  'station.addHint': 'It appears at the start position of {{plan}}; then drag it into place.',
  'station.deleteTitle': 'Delete {{code}}',
  'station.deleteBodyBefore': 'Are you sure you want to remove',
  'station.deleteBodyAfter': 'from {{plan}}?',
  'station.deleteNote': '{{code}} leaves the floor plan and the export. Its history is kept.',
  'common.optional': 'optional',
  'history.title': 'History of {{code}}',
  'history.created': 'Created',
  'history.moved': 'Moved',
  'history.rotated': 'Rotated',
  'history.updated': 'Changed',
  'history.deleted': 'Removed',
  'history.migrated': 'Taken over from Floorplanner',
  'history.migratedUpdate': 'Updated from Floorplanner',
  'history.removedInFloorplanner': 'no longer in the floorplanner',
  'history.adr_checked': 'ADR labels checked',
  'history.adrOk': '{{un}}: labels in order',
  'history.adrNotOk': '{{un}}: labels not in order',
  'history.inbound_dated': 'Inbound from the WMS',
  'history.inboundOn': '{{date}} at {{location}}',
  'history.nowLandscape': 'Now lying',
  'history.nowPortrait': 'Now standing',
  'history.field.code': 'Order number',
  'history.field.stationType': 'Type',
  'history.field.party': 'Customer',
  'history.field.floorplan': 'Floorplan',
  'history.field.position': 'Position',
  'history.field.owner': 'Owner',
  'history.migratedDetail': 'position, type and customer from v2.1.0',
  'history.system': 'System',
  'history.booking_mailed': 'Booking mail sent',
  'history.booking_not_mailed': 'Booking mail not sent',
  'history.booking_mail_failed': 'Booking mail failed',
  'history.bookingTo': '{{direction}} to {{to}}',
  'history.bookingReason.dry_run': 'dry run',
  'history.bookingReason.mail_not_configured': 'mail not set up',
  'history.bookingAttempts_one': 'after {{count}} attempt',
  'history.bookingAttempts_other': 'after {{count}} attempts',
  'history.note':
    'Every change is recorded with who and when. The history is kept, also after removal.',

  // Opslag — beheer: plattegrond instellen, stationtypen, kleuren (docs/storage.md)
  'nav.stationTypes': 'Types',
  'nav.storageOwners': 'Owners',
  // Opslag — het dashboard (docs/storage.md §20)
  'nav.storageDashboard': 'Dashboard',
  'storageDashboard.subtitle': 'Stock over time, time in storage and data quality of the storage.',
  'storageDashboard.title': 'Storage dashboard',
  'storageDashboard.asOf': 'As of {{date}}',
  'storageDashboard.week': 'wk {{week}} {{year}}',
  'storageDashboard.kpi.total': 'In storage now',
  'storageDashboard.kpi.adr': 'ADR not in order',
  'storageDashboard.kpi.adrUnchecked': 'of which {{count}} not yet checked',
  'storageDashboard.kpi.median': 'Median in storage',
  'storageDashboard.kpi.days': 'days',
  'storageDashboard.moves.title': 'In and out',
  'storageDashboard.moves.view': 'By type or by floor plan',
  'storageDashboard.moves.byType': 'By type',
  'storageDashboard.moves.byPlan': 'By floor plan',
  'storageDashboard.moves.day': 'Day',
  'storageDashboard.moves.week': 'Week',
  'storageDashboard.moves.month': 'Month',
  'storageDashboard.moves.weekTitle': 'Week {{week}} {{year}}',
  'storageDashboard.moves.prev.day': 'Previous day',
  'storageDashboard.moves.prev.week': 'Previous week',
  'storageDashboard.moves.prev.month': 'Previous month',
  'storageDashboard.moves.next.day': 'Next day',
  'storageDashboard.moves.next.week': 'Next week',
  'storageDashboard.moves.next.month': 'Next month',
  'storageDashboard.moves.moved_one':
    '{{count}} station moved to another floor plan; that counts as neither in nor out.',
  'storageDashboard.moves.moved_other':
    '{{count}} stations moved to another floor plan; that counts as neither in nor out.',
  'storageDashboard.moves.empty.day': 'Nothing booked in or out on this day.',
  'storageDashboard.moves.empty.week': 'Nothing booked in or out in this week.',
  'storageDashboard.moves.empty.month': 'Nothing booked in or out in this month.',
  'storageDashboard.moves.total': 'Total',
  'storageDashboard.cal.week': 'wk',
  'storageDashboard.cal.prevMonth': 'Previous month',
  'storageDashboard.cal.nextMonth': 'Next month',
  'storageDashboard.cal.prevYear': 'Previous year',
  'storageDashboard.cal.nextYear': 'Next year',
  'storageDashboard.cal.current.day': 'Today',
  'storageDashboard.cal.current.week': 'This week',
  'storageDashboard.cal.current.month': 'This month',
  'storageDashboard.kpi.withoutCustomer': 'Without customer',
  'storageDashboard.billing.title': 'For the invoice',
  'storageDashboard.billing.sub': 'average in storage per day',
  'storageDashboard.billing.how': 'How it counts',
  'storageDashboard.billing.howBody':
    'Every day counts what stood there that day, including the day it came in and the day it left. Added up, those are the item-days; divided by the number of days, that is the average.',
  'storageDashboard.billing.howToday':
    'This month counts up to and including today. A day without stock counts as 0.',
  'storageDashboard.billing.howSince':
    'When VHT did not count an owner for the whole month yet, the tile counts the days VHT knows, and says since when.',
  'storageDashboard.billing.thisMonth': '{{month}}, up to today',
  'storageDashboard.billing.lastMonth': '{{month}}',
  'storageDashboard.billing.sum_one': '{{itemDays}} item-days ÷ {{count}} day',
  'storageDashboard.billing.sum_other': '{{itemDays}} item-days ÷ {{count}} days',
  'storageDashboard.billing.since': 'VHT has counted {{owner}} since {{date}}',
  'storageDashboard.billing.none': 'VHT did not count {{owner}} yet',
  'storageDashboard.flow.title': 'Stock over time',
  'storageDashboard.flow.since': 'since {{date}}',
  'storageDashboard.flow.grain': 'Per week, month or year',
  'storageDashboard.grain.week': 'Week',
  'storageDashboard.grain.month': 'Month',
  'storageDashboard.grain.year': 'Year',
  'storageDashboard.flow.stock': 'In storage',
  'storageDashboard.flow.inOut': 'In and out',
  'storageDashboard.flow.in': 'In',
  'storageDashboard.flow.out': 'Out',
  'storageDashboard.flow.net': 'Net',
  'storageDashboard.flow.empty': 'Nothing in the history yet.',
  'storageDashboard.flow.stockBoth': 'In storage',
  'storageDashboard.flow.wms': 'Positions in the WMS',
  'storageDashboard.flow.vht': 'In VHT',
  'storageDashboard.flow.transition': 'Transition',
  'storageDashboard.flow.note':
    'Up to the transition on 24 October 2026 the stock comes from the WMS of TALIS: the positions at the end of each week on the four locations of the floor plans and Alfen’s station storage. The WMS counts positions, not stations, and other material too, so there is a step at the transition ({{positions}} positions against {{stations}} stations). From the transition VHT counts itself, with what came in and went out; what was already there counts as stock, not as arrivals.',
  'storageDashboard.flow.noteNoWms':
    'VHT counts bookings in and out since the transition on 24 October 2026; what was already there counts as stock, not as arrivals. The stock before then comes from the WMS, once it has been loaded.',
  'storageDashboard.flow.noteOwner':
    'VHT counts {{owner}} since {{date}}. What stood there before comes in once it is booked.',
  'storageDashboard.forecast.title': 'Forecast',
  'storageDashboard.forecast.label': 'Forecast',
  'storageDashboard.forecast.headline':
    'On {{date}} about {{mid}} stations expected, between {{low}} and {{high}}.',
  'storageDashboard.forecast.note':
    'This is how the stock moved in {{first}}–{{last}} from week {{week}}, over the thirteen weeks after, without the growth of those years: {{years}}. The middle ({{mid}}) is the line, the lowest and highest year the band, applied to what stands now. In summer and in frost less can be delivered, and that is in those years. What is already planned, or a winter that turns out differently, the forecast does not see.',
  'storageDashboard.forecast.inbound':
    'Over the last thirteen weeks, up to and including {{to}}, an average of {{mean}} per week came in from Alfen, between {{low}} and {{high}}. Up to the transition the WMS counts, after it VHT.',
  'storageDashboard.forecast.notForYear': 'The forecast is shown per week and per month.',
  'storageDashboard.forecast.otherOwner':
    'No forecast for {{owner}}: it works from the inbound of {{wms}} in the WMS, from before the transition. Choose {{wms}} to see it.',
  'storageDashboard.forecast.onlyOwner':
    '{{owner}} only: the forecast works from the inbound in the WMS, from before the transition, and that is of {{owner}}.',
  'storageDashboard.forecast.asTable': 'Forecast as a table',
  'storageDashboard.forecast.mid': 'Forecast',
  'storageDashboard.forecast.low': 'Lowest year',
  'storageDashboard.forecast.high': 'Highest year',
  'storageDashboard.asTable': 'As a table',
  'storageDashboard.col.period': 'Period',
  'storageDashboard.col.from': 'From',
  'storageDashboard.col.to': 'Up to and including',
  'storageDashboard.col.code': 'Order number',
  'storageDashboard.col.plan': 'Floor plan',
  'storageDashboard.col.customer': 'Customer',
  'storageDashboard.col.type': 'Type',
  'storageDashboard.col.owner': 'Owner',
  'storageDashboard.col.wmsRelation': 'In the WMS under',
  'storageDashboard.col.since': 'In storage since',
  'storageDashboard.col.days': 'Days',
  'storageDashboard.byType': 'By type',
  'storageDashboard.byPlan': 'Per floor plan',
  'storageDashboard.byCustomer': 'Per customer',
  'storageDashboard.noCustomer': 'Without customer',
  'storageDashboard.stay.title': 'Time in storage',
  'storageDashboard.stay.meta': 'mean {{mean}} · median {{median}} days',
  'storageDashboard.stay.buckets': 'How long they have been there',
  'storageDashboard.stay.longest': 'Longest in storage',
  'storageDashboard.stay.fromTransition_one':
    '{{count}} station counts from the transition on 24 October 2026, because the WMS has no inbound for it.',
  'storageDashboard.stay.fromTransition_other':
    '{{count}} stations count from the transition on 24 October 2026, because the WMS has no inbound for them.',
  'storageDashboard.bucket.0-30': '0–30 days',
  'storageDashboard.bucket.31-90': '31–90',
  'storageDashboard.bucket.91-180': '91–180',
  'storageDashboard.bucket.181-365': '181–365',
  'storageDashboard.bucket.366+': 'Over a year',
  'storageDashboard.quality.title': 'Data quality',
  'storageDashboard.quality.withoutCustomer': 'Without customer',
  'storageDashboard.quality.withoutCustomerHint':
    'Give them a customer on the floor plan, or Alfen cannot charge them on.',
  'storageDashboard.quality.notInWms': 'Not in the WMS',
  'storageDashboard.quality.notInWmsHint':
    'On a floor plan, but not in the WMS stock of {{time}}. Is it still there, and was it booked in?',
  'storageDashboard.quality.noSnapshot': 'No WMS stock snapshot received yet.',
  'storageDashboard.quality.otherRelation': 'Under another relation in the WMS',
  'storageDashboard.quality.otherRelationHint':
    'In the WMS stock of {{time}}, but not under the owner’s relation. Is the type in VHT right, or the relation in the WMS?',
  'storageDashboard.quality.withoutRelation_one':
    '{{names}} takes no part: no relation in the WMS under Storage › Owners.',
  'storageDashboard.quality.withoutRelation_other':
    '{{names}} take no part: no relation in the WMS under Storage › Owners.',
  'storageDashboard.quality.noRelation_one':
    'Not checked: {{names}} has no relation in the WMS. Fill it in under Storage › Owners.',
  'storageDashboard.quality.noRelation_other':
    'Not checked: {{names}} have no relation in the WMS. Fill it in under Storage › Owners.',
  'storageDashboard.quality.fromTransition': 'Without a real inbound date',
  'storageDashboard.quality.fromTransitionHint':
    'Counting from 24 October 2026: the WMS knows no inbound for them.',
  'storageDashboard.quality.show': 'Show which',
  'storageDashboard.quality.showFirst': 'Show the first {{count}}',
  'storage.newPlan': 'New floor plan',
  'storage.uploadDrawing': 'Upload drawing',
  'floorplan.settingsTitle': 'Floor plan settings',
  'floorplan.newTitle': 'New floor plan',
  'floorplan.newSub': 'A yard with a drawing, on which logistics places what is there.',
  'floorplan.details': 'Details',
  'floorplan.name': 'Name',
  'floorplan.namePlaceholder': 'E.g. Barneveld Nijverheidsweg',
  'floorplan.nameRequired': 'At least 2 characters.',
  'floorplan.loadingPlace': 'Loading place',
  'floorplan.noLoadingPlace': 'No loading place',
  'floorplan.loadingPlaceHelp':
    'Links the floor plan to the loading place of the staging list. Needed for the TALIS link later.',
  'floorplan.drawing': 'Drawing (SVG)',
  'floorplan.drop': 'Choose an SVG or drop it here',
  'floorplan.replace': 'Replace',
  'floorplan.notSavedYet': 'not saved yet',
  'floorplan.notSvg': 'This is not an SVG with a size (viewBox or width and height).',
  'floorplan.drawingHelp':
    'An SVG from Inkscape or AutoCAD. When replaced, the stations keep their coordinates.',
  'floorplan.stationSize': 'Block size',
  'floorplan.width': 'Width',
  'floorplan.height': 'Height',
  'floorplan.stationSizeHelp': 'Width × height in drawing units. Applies to every block here.',
  'floorplan.sizeInvalid': 'Two whole numbers between 5 and 500.',
  'floorplan.stationGap': 'Gap',
  'floorplan.stationGapHelp':
    'The space a dragged block keeps when it snaps beside or below a neighbour, in drawing units.',
  'floorplan.gapInvalid': 'A whole number from 0 to 100.',
  'floorplan.start': 'Start position',
  'floorplan.startHelp': 'Where something new appears. Tap the drawing to choose the spot.',
  'floorplan.startInvalid': 'Two whole numbers, 0 or more.',
  'floorplan.lat': 'Latitude',
  'floorplan.lon': 'Longitude',
  'floorplan.positionHelp': 'For the overview on the map.',
  'floorplan.positionInvalid':
    'Latitude between -90 and 90, longitude between -180 and 180 (comma or point).',
  'floorplan.deleteTitle': 'Delete floor plan',
  'floorplan.deleteBlocked_one': 'Only when nothing is left on it. Now: {{count}}.',
  'floorplan.deleteBlocked_other': 'Only when nothing is left on it. Now: {{count}}.',
  'floorplan.deleteConfirm':
    'Are you sure you want to delete {{name}}? The drawing goes with it; the history is kept.',
  'floorplan.previewTitle': 'Drawing',
  'floorplan.previewHint': 'tap to choose the start position',
  'floorplan.startLabel': 'Start',
  'floorplan.noPreview': 'Choose an SVG to see the drawing here.',
  'floorplan.saved': '{{name}} saved',
  'floorplan.deleted': '{{name}} deleted',
  'floorplan.drawingRefused': 'The floor plan was created. {{reason}}',
  'stationTypes.title': 'Types',
  'stationTypes.subtitle': 'The type sets the colour of a block on every floor plan.',
  'stationTypes.summary': '{{count}} types · {{stations}} in storage',
  'stationTypes.colorsInUse': '{{used}} of 12 colours in use',
  'stationTypes.ownerColors': '{{used}} of 12 colours',
  'stationTypes.new': 'New type',
  'stationTypes.newTitle': 'New type',
  'stationTypes.editTitle': 'Edit type',
  'stationTypes.colColor': 'Colour',
  'stationTypes.colName': 'Description',
  'stationTypes.owner': 'Owner',
  'stationTypes.ownerRequired': 'Choose an owner.',
  'stationTypes.article': 'Article from TALIS',
  'stationTypes.articleSearch': 'Search by code, description or WMS client',
  'stationTypes.articleNone': 'No active article found. Articles come from TALIS every hour.',
  'stationTypes.articleClear': 'Clear article',
  'stationTypes.articleHelp':
    'The article gives the sizes and the weight, and proposes the description and the UN number.',
  'stationTypes.articleInactive':
    'This article is no longer active in TALIS. The type stays; choose another if you like.',
  'stationTypes.adr': 'ADR (UN number)',
  'stationTypes.adrHelp': 'Empty: not dangerous goods. Greener batteries are UN3536.',
  'stationTypes.adrInvalid': 'An UN number is UN and four digits, e.g. UN3536.',
  'stationTypes.colStations': 'In storage',
  'stationTypes.name': 'Description',
  'stationTypes.namePlaceholder': 'E.g. Compact 630 kVA',
  'stationTypes.nameRequired': 'Required.',
  'stationTypes.color': 'Colour',
  'stationTypes.colorFree': 'Free',
  'stationTypes.colorFreeAt': 'Free · {{type}} at {{owner}}',
  'stationTypes.colorHelpHead':
    'Twelve colours, one per type of {{owner}}; the head sets them apart from {{plain}}. Rather not blue, petrol or berry: those are the big three there.',
  'stationTypes.colorCurrent': 'Current colour',
  'stationTypes.colorChosen': 'Chosen',
  'stationTypes.colorHelp':
    'Twelve colours, one per type. A colour in use comes free when its type is deleted.',
  'stationTypes.paletteFull': 'All twelve colours of {{owner}} are in use',
  'stationTypes.paletteFullFree':
    'First delete a type of {{owner}} with nothing in storage — now {{types}} — and its colour comes free.',
  'stationTypes.paletteFullNone':
    'Every type of {{owner}} has something in storage. Ask for the palette to be extended; do not invent a colour.',
  'stationTypes.inUse_one':
    '{{count}} in storage with this type. It can only be deleted once nothing of this type is left.',
  'stationTypes.inUse_other':
    '{{count}} in storage with this type. It can only be deleted once nothing of this type is left.',
  'stationTypes.saved': '{{name}} saved',
  'stationTypes.deleted': '{{name}} deleted',
  'storageOwners.title': 'Owners',
  'storageOwners.summary': '{{count}} owners · {{stations}} in storage',
  'storageOwners.new': 'New owner',
  'storageOwners.newTitle': 'New owner',
  'storageOwners.editTitle': 'Edit owner',
  'storageOwners.colOwner': 'Owner',
  'storageOwners.colShortName': 'Short name',
  'storageOwners.colCode': 'Debtor code',
  'storageOwners.colWmsRelation': 'Relation in the WMS',
  'storageOwners.noWmsRelation': 'none',
  'storageOwners.wmsPositions_one': '{{count}} position',
  'storageOwners.wmsPositions_other': '{{count}} positions',
  'storageOwners.wmsRelation': 'Relation in the WMS',
  'storageOwners.wmsRelationHelp':
    'What the WMS keeps this owner’s stock under, like 1000165 for Greener: not always the debtor code. Under Data quality the dashboard shows what the WMS has under another relation. Empty takes no part.',
  'storageOwners.colCodeRule': 'Code',
  'storageOwners.colCustomers': 'Customers',
  'storageOwners.colMark': 'Mark',
  'storageOwners.mark': 'head · {{letter}}',
  'storageOwners.noMark': 'none',
  'storageOwners.shortName': 'Short name',
  'storageOwners.shortNameAuto': 'The first word of the party',
  'storageOwners.shortNameHelp':
    'In buttons, headings and sentences, like Greener. At most 20 characters.',
  'storageOwners.shortNameRequired': 'Required.',
  'storageOwners.markLetter': 'Letter in the head',
  'storageOwners.markAuto': 'First free',
  'storageOwners.markHelpNew':
    'On every block of this owner, from 160 %. Empty: the first free letter of the short name.',
  'storageOwners.markHelp':
    'On every block, from 160 %. Empty is no head, like Alfen: only one owner can have that.',
  'storageOwners.markInvalid': 'One letter, A to Z.',
  'storageOwners.colTypes': 'Types',
  'storageOwners.colStations': 'In storage',
  'storageOwners.rule.ORDER_NUMBER': 'Order number',
  'storageOwners.rule.NAME': 'Name or batch',
  'storageOwners.ruleHelp.ORDER_NUMBER':
    '8 to 30 characters, in capitals, no spaces: as with Alfen.',
  'storageOwners.ruleHelp.NAME': 'At most 30 characters, as typed, no spaces: Leticia_253 or 001.',
  'storageOwners.ruleChanged':
    'Another rule applies to what is typed from now on. The codes that stand stay as they are.',
  'storageOwners.yes': 'Yes',
  'storageOwners.no': 'No',
  'storageOwners.party': 'Party',
  'storageOwners.partySearch': 'Search by debtor code or name',
  'storageOwners.partyNone':
    'No party found. Create it first under Admin › Parties, with its debtor code.',
  'storageOwners.partyRequired': 'Choose a party.',
  'storageOwners.codeRule': 'Code when booking',
  'storageOwners.hasCustomers': 'Can have a customer',
  'storageOwners.hasCustomersHelp':
    'With Alfen the customer is the grid operator. Greener has no customers: then the field is not there.',
  'storageOwners.inUse': 'This owner has {{count}} types. Delete those first to remove it.',
  'storageOwners.saved': '{{name}} saved',
  'storageOwners.deleted': '{{name}} is no longer an owner',
  'color.blue': 'Blue',
  'color.petrol': 'Petrol',
  'color.berry': 'Berry',
  'color.violet': 'Violet',
  'color.sky': 'Sky',
  'color.rose': 'Rose',
  'color.umber': 'Umber',
  'color.pine': 'Pine',
  'color.moss': 'Moss',
  'color.sand': 'Sand',
  'color.slate': 'Slate',
  'color.graphite': 'Graphite',

  // Quotation lists (docs/quotations.md §2, build list #2).
  'nav.quotationLists': 'Quotation lists',
  'quotationLists.title': 'Quotation lists',
  'quotationLists.tab.craneTypes': 'Crane types',
  'quotationLists.tab.articles': 'Articles',
  'quotationLists.tab.standardTexts': 'Standard texts',
  'quotationLists.tab.addresses': 'Addresses',
  'quotationLists.searchPlaceholder.craneTypes': 'Search by name',
  'quotationLists.searchPlaceholder.articles': 'Search by code, description or client',
  'quotationLists.searchPlaceholder.standardTexts': 'Search by name or text',
  'quotationLists.searchPlaceholder.addresses': 'Search by company, address or city',
  'quotationLists.includeInactive': 'Also taken off the list',
  'quotationLists.allKinds': 'All kinds',
  'quotationLists.download': 'Download Excel',
  'quotationLists.downloadFailed': 'The download failed. Try again.',
  'quotationLists.import': 'Import Excel',
  'quotationLists.new.craneTypes': 'New crane type',
  'quotationLists.new.articles': 'New article',
  'quotationLists.new.standardTexts': 'New standard text',
  'quotationLists.new.addresses': 'New address',
  'quotationLists.editTitle.craneTypes': 'Edit crane type',
  'quotationLists.editTitle.articles': 'Edit article',
  'quotationLists.editTitle.standardTexts': 'Edit standard text',
  'quotationLists.editTitle.addresses': 'Edit address',
  'quotationLists.col.name': 'Name',
  'quotationLists.col.code': 'Code',
  'quotationLists.col.description': 'Description',
  'quotationLists.col.sizes': 'l × w × h (m)',
  'quotationLists.col.weight': 'Weight (kg)',
  'quotationLists.col.kind': 'Kind',
  'quotationLists.col.text': 'Text',
  'quotationLists.col.updated': 'Changed',
  'quotationLists.col.company': 'Company',
  'quotationLists.col.street': 'Street',
  'quotationLists.col.place': 'Postcode and city',
  'quotationLists.col.country': 'Country',
  'quotationLists.offList': 'off the list',
  'quotationLists.defaultChip': 'default',
  'quotationLists.empty.craneTypes': 'No crane types yet',
  'quotationLists.empty.articles': 'No articles yet',
  'quotationLists.empty.standardTexts': 'No standard texts yet',
  'quotationLists.empty.addresses': 'No fixed addresses yet',
  'quotationLists.emptyHint':
    'Add one, or import an Excel file. Download it first: that is the template.',
  'quotationLists.noMatch': 'Nothing found',
  'quotationLists.noMatchHint': 'Search for something else, or turn on "Also taken off the list".',
  'quotationLists.kind.INTRO': 'Intro',
  'quotationLists.kind.CRANE': 'Crane text',
  'quotationLists.kind.BODY': 'Quotation text',
  'quotationLists.kind.OPTIONS': 'Options note',
  'quotationLists.kind.CLOSING': 'Closing text',
  'quotationLists.kind.EXCEPTIONAL': 'Exceptional transport',
  'quotationLists.kind.PRICE_REQUEST': 'Price request',
  'quotationLists.kindHint.INTRO': 'Above the lines.',
  'quotationLists.kindHint.CRANE': 'With a crane, below the crane type.',
  'quotationLists.kindHint.BODY': 'Below the lines, above the totals.',
  'quotationLists.kindHint.OPTIONS': 'Only when the quotation has an option.',
  'quotationLists.kindHint.CLOSING': 'At the bottom: validity and payment terms.',
  'quotationLists.kindHint.EXCEPTIONAL': 'With exceptional transport: the route is an indication.',
  'quotationLists.kindHint.PRICE_REQUEST': 'In the price request (later).',
  'quotationLists.field.name': 'Name',
  'quotationLists.field.craneNamePlaceholder': 'For example Telekraan 300 ton',
  'quotationLists.field.code': 'Code',
  'quotationLists.field.description': 'Description',
  'quotationLists.field.length': 'Length (m)',
  'quotationLists.field.width': 'Width (m)',
  'quotationLists.field.height': 'Height (m)',
  'quotationLists.field.weight': 'Weight (kg)',
  'quotationLists.field.sizesHint':
    'The standard sizes. A quotation can change them per article. Empty is fine.',
  'quotationLists.field.kind': 'Kind',
  'quotationLists.field.body': 'Text',
  'quotationLists.field.textNamePlaceholder': 'For example After a phone call',
  'quotationLists.field.textNameHint': 'How you choose it in a quotation.',
  'quotationLists.field.isDefault': 'Default in a new quotation',
  'quotationLists.field.company': 'Company',
  'quotationLists.field.street': 'Street and number',
  'quotationLists.field.postcode': 'Postcode',
  'quotationLists.field.city': 'City',
  'quotationLists.field.country': 'Country',
  'quotationLists.addressHint':
    'A fixed address for loading or unloading. A quotation takes a copy: changing it here changes no quotation that has it.',
  'quotationLists.requiredCompanyCity': 'Company and city are required.',
  'quotationLists.field.isDefaultHint':
    'At most one per kind. If another text of this kind was the default, it no longer is.',
  'quotationLists.invalidSize': 'Not a size. Type a number in metres, such as 3,80.',
  'quotationLists.invalidWeight': 'Not a weight. Type a number in kilograms, such as 21000.',
  'quotationLists.requiredCodeDescription': 'Code and description are required.',
  'quotationLists.duplicateName': 'This name is on the list already.',
  'quotationLists.duplicateCode': 'This code is on the list already.',
  'quotationLists.duplicateAddress': 'This company is on the list in this city already.',
  'quotationLists.takeOff': 'Take off the list',
  'quotationLists.putBack': 'Put back',
  'quotationLists.offNote':
    'Taken off the list: it cannot be chosen in a new quotation. A quotation that has it, keeps it.',
  'quotationLists.saved': '{{name}} saved',
  'quotationLists.takenOff': '{{name}} taken off the list',
  'quotationLists.putBackDone': '{{name}} is on the list again',
  'quotationLists.importTitle': 'Import Excel',
  'quotationLists.importIntro':
    'Choose a file like the download: tabs Kraantypes, Artikelen, Standaardteksten and Adressen. First you see what changes; only when you click Import is it read in. What is not in the file stays. Articles from TALIS are not in it: change those in TALIS.',
  'quotationLists.importChoose': 'Choose file',
  'quotationLists.importOther': 'Other file',
  'quotationLists.importReading': 'Reading the file…',
  'quotationLists.importNotXlsx': 'This is not an Excel file (.xlsx).',
  'quotationLists.importLine': '{{created}} new · {{updated}} changed · {{unchanged}} the same',
  'quotationLists.importNew': 'New',
  'quotationLists.importChanged': 'Changed',
  'quotationLists.importNotInFile': 'Not in the file; stays as it is.',
  'quotationLists.importProblems':
    'Problems in the file: {{count}}. Nothing is read in until they are solved.',
  'quotationLists.importMore': 'and {{count}} more',
  'quotationLists.importNothing': 'The file changes nothing.',
  'quotationLists.importReady': 'Click Import to make these changes.',
  'quotationLists.importApply': 'Import',
  'quotationLists.importApplying': 'Importing…',
  'quotationLists.importDone': 'Imported: {{created}} new, {{updated}} changed',
  'quotationLists.importCol.list': 'Tab',
  'quotationLists.importCol.row': 'Row',
  'quotationLists.importCol.column': 'Column',
  'quotationLists.importCol.problem': 'Problem',
  'quotationLists.problem.not_a_lists_file':
    'No tab Kraantypes, Artikelen or Standaardteksten found.',
  'quotationLists.problem.missing_column': 'This column is missing.',
  'quotationLists.problem.required': 'Empty, but required.',
  'quotationLists.problem.too_long': 'Too long.',
  'quotationLists.problem.invalid_number': 'Not a number: "{{value}}".',
  'quotationLists.problem.out_of_range': 'Too large or too small: "{{value}}".',
  'quotationLists.problem.invalid_yes_no': 'Not yes or no: "{{value}}".',
  'quotationLists.problem.unknown_kind': 'Unknown kind: "{{value}}".',
  'quotationLists.problem.invalid_country': 'No country code such as NL, BE or DE: "{{value}}".',
  'quotationLists.problem.duplicate': 'Twice in the file.',
  'quotationLists.problem.two_defaults': 'Two default texts of the same kind.',
  'quotationLists.problem.default_inactive': 'A default text cannot be off the list.',

  // Quotations (docs/quotations.md, build list #3): the overview and the workbench.
  'nav.quotations': 'Quotations',
  'relations.pickThis': 'Choose this one',
  'quotations.title': 'Quotations',
  'quotations.subtitle': 'Quotations for transport and hoisting, with versions',
  'quotations.titleOf': 'Quotation {{number}}',
  'quotations.searchPlaceholder': 'Search by number, relation or reference',
  'quotations.state.concept': 'Draft',
  'quotations.state.issued': 'Issued',
  'quotations.state.expired': 'Expired',
  'quotations.new': 'New quotation',
  'quotations.createFailed': 'The quotation could not be created.',
  'quotations.col.number': 'Number',
  'quotations.col.relation': 'Relation',
  'quotations.col.type': 'Type',
  'quotations.col.reference': 'Your reference',
  'quotations.col.amount': 'Quotation amount',
  'quotations.col.options': 'Options',
  'quotations.col.changed': 'Changed',
  'quotations.col.quantity': 'Qty',
  'quotations.col.article': 'Article',
  'quotations.col.length': 'l (m)',
  'quotations.col.width': 'w (m)',
  'quotations.col.height': 'h (m)',
  'quotations.col.weight': 'kg',
  'quotations.col.articleAmount': 'Amount',
  'quotations.col.optional': 'Opt.',
  'quotations.type.TRANSPORT': 'Transport',
  'quotations.type.HOISTING': 'Hoisting',
  'quotations.noRelation': 'No relation yet',
  'quotations.attention': 'attn. {{name}}',
  'quotations.yourReference': 'your reference {{reference}}',
  'quotations.empty': 'No quotations yet',
  'quotations.emptyHint': 'Make one with "New quotation". It gets today’s number.',
  'quotations.noMatch': 'No quotations found',
  'quotations.noMatchHint': 'Nothing matches this search or status.',
  'quotations.clearFilter': 'Clear filter',
  'quotations.neverIssued': 'not issued yet',
  'quotations.versionValidUntil': 'version {{version}} valid until {{date}}',
  'quotations.versionExpired': 'version {{version}} expired',
  'quotations.validUntil': 'valid until {{date}}',
  'quotations.expiredSince': 'since {{date}}',
  'quotations.allQuotations': 'All quotations',
  'quotations.notFound': 'This quotation does not exist, or you may not see it.',
  'quotations.versions': 'Versions',
  'quotations.versionN': 'Version {{n}}',
  'quotations.conflict': '{{name}} saved version {{version}} at {{time}}, while you had it open.',
  'quotations.conflictSelf': 'You saved version {{version}} at {{time}} in another window.',
  'quotations.conflictChanged': 'Changed: {{steps}}.',
  'quotations.conflictPaused': 'Your own changes are saved again once you choose.',
  'quotations.takeTheirs': 'Load the version of {{name}}',
  'quotations.takeSaved': 'Load the saved version',
  'quotations.keepMine': 'Keep my version',
  'quotations.lockedMeanwhile':
    'This version has been issued in the meantime and is fixed. Your last changes were not saved.',
  'quotations.reload': 'Reload',
  'quotations.leaveTitle': 'Whose version stays?',
  'quotations.leaveBody':
    '{{name}} saved version {{version}} at the same time as you. Your last changes are therefore not saved yet. If you save your version, what {{name}} changed is lost.',
  'quotations.stay': 'Stay',
  'quotations.leaveTheirs': 'Keep the version of {{name}}',
  'quotations.leaveMine': 'Save my version',
  'quotations.step.customer': 'Customer',
  'quotations.step.customerSub': 'Who the quotation goes to',
  'quotations.step.lines': 'Lines',
  'quotations.step.linesSub.TRANSPORT': 'Loading, unloading, crane and articles',
  'quotations.step.linesSub.HOISTING': 'Unloading, crane and articles',
  'quotations.step.texts': 'Text',
  'quotations.step.textsSub': 'One text below the amounts, added to with standard texts',
  'quotations.step.internal': 'Internal',
  'quotations.step.internalSub': 'Never in the quotation or in a mail',
  'quotations.relation': 'Relation',
  'quotations.searchRelation': 'Search by name, code, city or contact person',
  'quotations.noRelationFound': 'No relation found.',
  'quotations.notThere': 'Not in the list?',
  'quotations.makeProspect': 'Make a prospect',
  'quotations.prospectMade':
    'Prospect {{name}} made. If it becomes a customer, the relation comes back through Exact and TALIS and you link it.',
  'quotations.contact': 'For the attention of',
  'quotations.chooseRelationFirst': 'Choose a relation first',
  'quotations.noContacts': 'No contact persons yet',
  'quotations.chooseContact': 'Choose a contact person',
  'quotations.inVht': 'in VHT',
  'quotations.contactAdded': '{{name}} added to {{relation}}.',
  'quotations.reference': 'Your reference',
  'quotations.referencePlaceholder': 'The customer’s reference, e.g. a purchase order number',
  'quotations.typeLabel': 'Type',
  'quotations.hoistingHint': 'Hoisting has only an unloading place per line.',
  'quotations.lineCount_one': '{{count}} line',
  'quotations.lineCount_other': '{{count}} lines',
  'quotations.line': 'Line {{n}}',
  'quotations.articleCount_one': '{{count}} article',
  'quotations.articleCount_other': '{{count}} articles',
  'quotations.lineOptional': 'Whole line optional',
  'quotations.copyLine': 'Copy line',
  'quotations.copyLineN': 'Copy line {{n}}',
  'quotations.removeLine': 'Remove line',
  'quotations.removeLineN': 'Remove line {{n}}',
  'quotations.addLine': 'Add line',
  'quotations.place.load': 'Loading',
  'quotations.place.unload': 'Unloading',
  'quotations.field.company': 'Company name',
  'quotations.fixedAddress': 'Fixed address',
  'quotations.searchAddress': 'Choose a fixed address to fill the fields',
  'quotations.noAddress': 'No fixed address found.',
  'quotations.craneAt.load': 'Crane at loading',
  'quotations.craneAt.unload': 'Crane at unloading',
  'quotations.craneType': 'Crane type',
  'quotations.chooseCrane': 'Choose a crane',
  'quotations.amount': 'Amount',
  'quotations.craneAmount.load': 'Amount crane at loading',
  'quotations.craneAmount.unload': 'Amount crane at unloading',
  'quotations.optional': 'Optional',
  'quotations.optionalHint': 'then not part of the quotation amount',
  'quotations.craneText': 'Text with the crane',
  'quotations.price': 'Price',
  'quotations.priceOfLine': 'Price of line {{n}}',
  'quotations.pricing.ARTICLE': 'Per article',
  'quotations.pricing.LINE': 'For the whole line',
  'quotations.articleN': 'article {{n}}',
  'quotations.searchArticle': 'Search by code, description or client',
  'quotations.noArticle': 'No article found.',
  'quotations.amountOfArticle': 'Amount article {{n}}',
  'quotations.articleOptional': 'Article {{n}} optional',
  'quotations.removeArticle': 'Remove article',
  'quotations.removeArticleN': 'Remove article {{n}}',
  'quotations.addArticle': 'Article',
  'quotations.articleTextPlaceholder':
    'Text under the article, e.g. unloaded incl. 2 loading and 2 unloading hours',
  'quotations.articleText': 'Text with article {{n}}',
  'quotations.sizesAdjusted': 'sizes adjusted',
  'quotations.sizesFromList': 'back to the list',
  'quotations.fromList': 'From the list: {{sizes}} m, {{kg}} kg',
  'quotations.inAmount': 'In the quotation amount',
  'quotations.options': 'Options',
  'quotations.standardText': 'Standard text',
  'quotations.textInserted': 'Standard text inserted.',
  'quotations.undo': 'Undo',
  'quotations.text': 'Text in the quotation',
  'quotations.textHint.body':
    'Goes below the amounts. A standard text is added at the end; a blank line starts a new paragraph.',
  'quotations.calculation': 'Calculation',
  'quotations.calculationPlaceholder': 'Calculate here: hours, kilometres, the cost of a crane…',
  'quotations.calculationHint':
    'Only for whoever makes the quotation. Not in the PDF and never in a mail.',
  'quotations.side': 'The quotation',
  'quotations.amountMain': 'Quotation amount',
  'quotations.exclOptions': 'excl. options',
  'quotations.exclVat': 'All amounts exclude VAT.',
  'quotations.issuesTitle': 'Still needed before issuing',
  'quotations.complete': 'Everything the quotation needs is in it.',
  'quotations.private': 'Calculation, driving time and toll are not in the quotation.',
  'quotations.saving': 'Saving…',
  'quotations.saved': 'Draft saved {{when}}.',
  'quotations.savedBy': 'Draft saved {{when}} by {{who}}.',
  'quotations.savePaused': 'Saving is paused until you choose whose version stays.',
  'quotations.saveFailed': 'Saving failed.',
  'quotations.saveLocked': 'This version is fixed.',
  'quotations.historyTitle': 'Who worked on it',
  'quotations.historySub': 'From the audit: who made, edited, issued and downloaded the quotation.',
  'quotations.event.created': 'Quotation created',
  'quotations.event.saved': 'Version {{version}} edited',
  'quotations.event.overwrote': 'Version {{version}} saved, over the one of {{name}}',
  'quotations.showAll': 'Show all ({{count}})',
  'quotations.showLess': 'Show less',
  'quotations.discard': 'Throw away draft',
  'quotations.discardTitle': 'Throw away draft {{number}}?',
  'quotations.discardBody':
    'This quotation was never issued. It disappears from the overview and its number does not come back. The audit keeps what it was and who threw it away.',
  'quotations.discardConfirm': 'Throw away',
  'quotations.discarded': 'Quotation {{number}} has been thrown away.',
  'quotations.discardFailed': 'Throwing it away failed.',
  'quotations.goneMeanwhile':
    'This quotation has been thrown away in the meantime. What you changed here was not saved.',
  'quotations.saveGone': 'This quotation no longer exists.',
  'quotations.issue.inLine': 'Line {{n}}: {{text}}',
  'quotations.issue.noRelation': 'choose a relation.',
  'quotations.issue.noContacts': 'add a contact person to {{relation}}.',
  'quotations.issue.noContact': 'choose for whose attention.',
  'quotations.issue.loadCity': 'fill in the city of the loading place.',
  'quotations.issue.unloadCity': 'fill in the city of the unloading place.',
  'quotations.issue.loadCraneType': 'choose the crane type at loading.',
  'quotations.issue.unloadCraneType': 'choose the crane type at unloading.',
  'quotations.issue.loadCraneAmount': 'fill in the amount of the crane at loading.',
  'quotations.issue.unloadCraneAmount': 'fill in the amount of the crane at unloading.',
  'quotations.issue.noArticle': 'add an article.',
  'quotations.issue.articleNotChosen': 'choose article {{n}}.',
  'quotations.issue.articleQuantity': 'fill in the quantity of {{name}}.',
  'quotations.issue.articleAmount': 'fill in the amount of {{name}}.',
  'quotations.issue.lineAmount': 'fill in the price of the line.',
  'quotations.issue.text': 'fill in the text (validity and payment).',
  'quotations.issue.noLine': 'add a line.',
  'quotations.issue.conflict': 'first choose whose version stays: {{name}} saved at the same time.',
  // Issuing and the PDF (build list #4).
  'quotations.sheet': 'As the customer gets it',
  'quotations.sheetBuilding': 'Updating…',
  'quotations.sheetFailed': 'The preview failed.',
  'quotations.sheetPages_one': '{{count}} page',
  'quotations.sheetPages_other': '{{count}} pages',
  'quotations.sheetPage': 'Page {{n}}',
  'quotations.sheetWaiting': 'Drawing the quotation.',
  'quotations.issueVersion': 'Issue version {{n}}',
  'quotations.previewPdf': 'Preview PDF',
  'quotations.previewMaking': 'Making the preview…',
  'quotations.previewFile': 'Concept quotation {{reference}}.pdf',
  'quotations.previewFailed': 'The preview could not be made.',
  'quotations.issueTitle': 'Issue version {{n}}?',
  'quotations.issueTo': 'To',
  'quotations.issueReference': 'Quotation',
  'quotations.issueAmount': 'Amount',
  'quotations.issueAmountText': '{{main}} excl. options',
  'quotations.issueAmountWithOptions': '{{main}} excl. options · options {{options}}',
  'quotations.issueValidUntil': 'Valid until',
  'quotations.issueBody':
    'After this, version {{n}} is fixed. The PDF is kept with the terms behind it, and so are the name and address of the relation as they are now. Changes can then only go into a new version.',
  'quotations.issueConfirm': 'Issue',
  'quotations.issuing': 'Issuing…',
  'quotations.issuedToast':
    'Quotation {{reference}} is issued. Download the PDF and mail it yourself.',
  'quotations.issueFailed': 'Issuing failed.',
  'quotations.issueIncomplete': 'Cannot issue yet: something is missing from the quotation.',
  'quotations.issuedAlert': 'Issued {{when}} by {{who}}. Valid until {{until}}.',
  'quotations.expiredAlert':
    'Expired on {{until}}. If the customer still wants it, issue a new version; that is valid for another 30 days.',
  'quotations.downloadPdf': 'Download PDF',
  'quotations.downloadFailed': 'The download failed.',
  'quotations.newVersion': 'New version',
  'quotations.newVersionMade': 'Version {{n}} made, as a copy of version {{from}}.',
  'quotations.newVersionFailed': 'Making a new version failed.',
  'quotations.conceptIs': 'Version {{n}} is the concept being worked on.',
  'quotations.lockedBanner': 'Version {{n}} was issued {{when}} by {{who}} and is fixed.',
  'quotations.lockedEditIn': 'Changes go into version {{n}}, the concept.',
  'quotations.lockedEditNew': 'Changes go into a new version.',
  'quotations.toVersion': 'To version {{n}}',
  'quotations.event.issued': 'Version {{version}} issued',
  'quotations.event.downloaded': 'Version {{version}} downloaded',
  'quotations.event.version': 'Version {{version}} made, as a copy of version {{from}}',
  // Mailing (§7): for now to sales@, which forwards it.
  'quotations.mail.button': 'Mail version {{n}}',
  'quotations.mail.again': 'Mail version {{n}} again',
  'quotations.mail.title': 'Mail version {{n}}?',
  'quotations.mail.to': 'To',
  'quotations.mail.for': 'For',
  'quotations.mail.attachment': 'Attachment',
  'quotations.mail.replyTo': 'Replies to',
  'quotations.mail.bcc': 'Bcc',
  'quotations.mail.bodyDirect':
    'The mail goes to {{to}}, with the quotation attached, and {{bcc}} gets a copy (bcc).',
  'quotations.mail.redirect':
    'In this environment every mail goes to {{to}}, with who it was meant for at the top.',
  'quotations.mail.body':
    'The mail goes to {{to}}, with the quotation attached and who it is for at the top. Forward it from there to the customer.',
  'quotations.mail.dryRun': 'Trial mode: nothing is really mailed in this environment.',
  'quotations.mail.send': 'Mail',
  'quotations.mail.sending': 'Working…',
  'quotations.mail.queuedToast': 'The quotation is on its way to {{to}}.',
  'quotations.mail.failedToast': 'Mailing failed.',
  'quotations.mail.afterIssue': 'Once issued, mail it to {{to}}, or download the PDF.',
  'quotations.mail.afterIssueDirect':
    'Once issued, mail it to the contact person, with a copy (bcc) to {{bcc}}, or download the PDF.',
  'quotations.mail.offAfterIssue':
    'Mailing is not set up here: once issued, download the PDF and mail it yourself.',
  'quotations.mail.off': 'Mailing is not set up here: download the PDF and mail it yourself.',
  'quotations.mail.dryRunNote':
    'Trial mode: the mail would go to {{to}}, but nothing is really mailed.',
  'quotations.mail.goesTo': 'Goes to {{to}}; forward it from there to {{name}}.',
  'quotations.mail.goesToDirect': 'Goes to {{to}}, with a copy (bcc) to {{bcc}}.',
  'quotations.mail.dryRunNoteDirect':
    'Trial mode: the mail would go to {{to}}, with a copy (bcc) to {{bcc}}, but nothing is really mailed.',
  'quotations.mail.customer': 'the customer',
  'quotations.mail.status.QUEUED': 'Sending',
  'quotations.mail.status.SENT': 'Sent',
  'quotations.mail.status.FAILED': 'Failed',
  'quotations.mail.status.NOT_SENT': 'Not sent',
  'quotations.mail.queued': 'The quotation is on its way to {{to}}.',
  'quotations.mail.sent': 'Mailed to {{to}} at {{time}}.',
  'quotations.mail.sentOn': 'Mailed to {{to}} on {{date}}.',
  'quotations.mail.failed':
    'The mail to {{to}} failed after several tries. Download the PDF and mail it yourself, and tell the admins.',
  'quotations.mail.notSent.dry_run': 'Mailing is in trial mode in this environment.',
  'quotations.mail.notSent.mail_not_configured': 'Mailing is not set up in this environment.',
  'quotations.event.mailed': 'Version {{version}} mailed to {{to}}',
  // Route en kaart (bouwlijst #5).
  'quotations.route.title': 'Route and CO₂',
  'quotations.route.hoisting': 'Hoisting has no route.',
  'quotations.route.off': 'Calculating is off in this environment: HERE has no key here.',
  'quotations.route.showCo2': 'State the CO₂ emissions in the quotation',
  'quotations.route.showCo2Hint':
    'Under the map: “Estimated CO₂ emissions of this transport: … kg”. The calculation stays internal.',
  'quotations.route.calculate': 'Calculate',
  'quotations.route.again': 'Calculate again',
  'quotations.route.calculating': 'Working…',
  'quotations.route.needPlaces': 'Fill in the city of loading and of unloading first.',
  'quotations.route.none': 'Not calculated yet. Without a route the quotation has no map.',
  'quotations.route.noneIssued': 'Issued without a route: the quotation has no map.',
  'quotations.route.distance': 'Distance',
  'quotations.route.km': '{{km}} km',
  'quotations.route.duration': 'Driving time',
  'quotations.route.hours': '{{time}} h',
  'quotations.route.toll': 'Toll · whole trip',
  'quotations.route.tollIn': 'Toll {{country}} · whole trip',
  'quotations.route.noToll': 'none',
  'quotations.route.kg': '{{kg}} kg',
  'quotations.route.found':
    'Loading: {{from}} ({{fromPrecision}}). Unloading: {{to}} ({{toPrecision}}).',
  'quotations.route.precision.houseNumber': 'address',
  'quotations.route.precision.street': 'street',
  'quotations.route.precision.postalCodePoint': 'postcode',
  'quotations.route.precision.locality': 'only the town',
  'quotations.route.precision.intersection': 'intersection',
  'quotations.route.precision.place': 'place',
  'quotations.route.precision.administrativeArea': 'only the area',
  'quotations.route.vehicle.trekker-oplegger-40t-5as-euro6':
    'tractor with semi-trailer, 40 t, 5 axles, Euro 6',
  'quotations.route.made':
    'Truck: {{vehicle}}, there and back from {{depot}}. Calculated {{when}} by {{who}}, in version {{version}}. A proposal for the calculation, not a price. Only the map and the kilometres of the loaded trip go into the quotation.',
  'quotations.route.co2How':
    'CO₂: {{tons}} t × {{km}} km × {{factor}} kg per tonne-km ({{name}}, well-to-wheel, emissiefactoren.nl {{year}}). Only the loaded trip; the weight comes from the articles that are not an option.',
  'quotations.route.co2NoFactor': 'No CO₂: Fuel › Emission factors has no factor for this truck.',
  'quotations.route.co2NoWeight': 'No CO₂: the articles have no weight yet.',
  'quotations.route.stale':
    'Loading, unloading or the truck changed after calculating. Calculate again, or the map and the toll will be wrong.',
  'quotations.route.remove': 'Remove the route (then no map in the quotation)',
  'quotations.route.exceptional': 'Exceptional transport',
  'quotations.route.exceptionalHint':
    'HERE calculates with the chosen truck, not with the permitted route. The quotation gets the text that the route is an indication (step 3).',
  'quotations.route.exceptionalChip': 'exceptional',
  'quotations.route.error.LOAD_NOT_FOUND':
    'HERE cannot find the loading place. Check street, postcode, city and country.',
  'quotations.route.error.UNLOAD_NOT_FOUND':
    'HERE cannot find the unloading place. Check street, postcode, city and country.',
  'quotations.route.error.LOAD_INCOMPLETE': 'Fill in the city of loading first.',
  'quotations.route.error.UNLOAD_INCOMPLETE': 'Fill in the city of unloading first.',
  'quotations.route.error.NO_ROUTE': 'HERE finds no truck route between these places.',
  'quotations.route.error.ROUTE_OFF': 'Calculating is off in this environment.',
  'quotations.route.error.other':
    'Calculating did not work: HERE cannot be reached right now. Try again shortly.',
  'quotations.textHint.exceptional': 'In the quotation because a line is exceptional (step 4).',
  'quotations.issue.routeStale':
    'calculate the route again: loading, unloading or the truck changed.',
  'quotations.issue.exceptionalText': 'fill in the text for exceptional transport.',
  'quotations.event.route':
    'Route calculated in version {{version}}, line {{line}}: {{from}} → {{to}}, {{km}} km',
  'quotations.route.co2Loaded': 'CO₂ · loaded trip',
  'quotations.route.leg': 'Part of the trip',
  'quotations.route.legs.approach': 'Driving there',
  'quotations.route.legs.loaded': 'Loaded trip (in the quotation)',
  'quotations.route.legs.return': 'Back',
  'quotations.route.total': 'Whole trip',
  'quotations.route.home': 'Hoevelaken',
  'quotations.route.adjust': 'Adjust',
  'quotations.route.adjustOf.approach': 'Adjust the approach',
  'quotations.route.adjustOf.return': 'Adjust the return',
  'quotations.route.byHere': 'HERE: {{value}}',
  'quotations.route.adjustHint':
    "Adjust, for the approach and the return: 50% counts half the kilometres and half the driving time of that part (for instance when the truck comes empty from another job halfway); a time such as 3:00 counts that driving time, and the kilometres stay HERE's. The toll does not change with it.",
  'quotations.route.tollWhere': 'Toll',
  'quotations.route.tollLeg.approach': 'Approach',
  'quotations.route.tollLeg.loaded': 'Loaded trip',
  'quotations.route.tollLeg.return': 'Return',
  'quotations.route.tollTotal': 'Total toll',
  'quotations.route.tollOld':
    'This route was calculated before VHT kept the toll per part of the trip. Calculate again to see where the toll is paid.',
  'quotations.route.tollHow':
    'How HERE calculates the toll: for {{truck}}, the cheapest way to pay each toll road, at the rates of {{when}}. The approach and the return count, so a toll road on the way there and back is in it twice. The toll is not in the quotation.',
  // Vrachtwagens, laad- en lostijd (bouwlijst #5).
  'quotationLists.tab.truckTypes': 'Trucks',
  'quotationLists.searchPlaceholder.truckTypes': 'Search by name',
  'quotationLists.new.truckTypes': 'New truck',
  'quotationLists.editTitle.truckTypes': 'Edit truck',
  'quotationLists.empty.truckTypes': 'No trucks yet',
  'quotationLists.col.truckKind': 'Kind',
  'quotationLists.col.grossWeight': 'Weight',
  'quotationLists.col.axles': 'Axles',
  'quotationLists.col.euro': 'Euro',
  'quotationLists.col.co2Factor': 'CO₂ factor',
  'quotationLists.truckKind.TRACTOR': 'tractor',
  'quotationLists.truckKind.STRAIGHT_TRUCK': 'straight truck',
  'quotationLists.truckKindName.TRACTOR': 'Tractor (with semi-trailer)',
  'quotationLists.truckKindName.STRAIGHT_TRUCK': 'Straight truck',
  'quotationLists.field.truck.kind': 'Kind',
  'quotationLists.field.truck.weight': 'Gross weight (kg)',
  'quotationLists.field.truck.axles': 'Axles',
  'quotationLists.field.truck.trailers': 'Trailers',
  'quotationLists.field.truck.euro': 'Euro class',
  'quotationLists.field.truck.height': 'Height (m)',
  'quotationLists.field.truck.width': 'Width (m)',
  'quotationLists.field.truck.length': 'Length (m)',
  'quotationLists.field.truck.weightHint':
    'The weight with trailer and load, and the axles of the whole combination: route and toll depend on them. A semi-trailer counts as a trailer.',
  'quotationLists.field.truck.sizesHint':
    'May stay empty. Filled in, HERE does not route under a bridge or through a tunnel the truck does not fit.',
  'quotationLists.field.truck.co2Factor': 'CO₂ factor',
  'quotationLists.field.truck.co2FactorHint':
    'From Fuel › Emission factors, per tonne-km. The quotation calculates the CO₂ of the loaded trip with it.',
  'quotationLists.field.truck.isDefault':
    'Default: a line that chose no truck yet calculates with it',
  'quotationLists.factorOption': '{{name}} — {{factor}} kg per tonne-km ({{year}})',
  'quotationLists.duplicateTruck': 'A truck with this name is on the list already.',
  'quotationLists.invalidTruck':
    'Fill in a name, a weight of 1,000 to 300,000 kg, 2 to 20 axles and 0 to 5 trailers. A height or width is 1 to 6 m, a length 3 to 60 m, or empty.',
  'quotationLists.filterSource': 'Source',
  'quotationLists.source.TALIS': 'TALIS',
  'quotationLists.source.VHT': 'VHT',
  'quotationLists.col.source': 'Source',
  'quotationLists.talis.none':
    'No articles have been received from TALIS yet. Once the link through DataBridge is switched on, they come in every hour; until then only the articles of VHT are here.',
  'quotationLists.talis.last_one': 'Last updated from TALIS: {{when}} · {{count}} article',
  'quotationLists.talis.last_other': 'Last updated from TALIS: {{when}} · {{count}} articles',
  'quotationLists.talis.vht_one': '{{count}} of VHT',
  'quotationLists.talis.vht_other': '{{count}} of VHT',
  'quotationLists.talis.rejected':
    'The last delivery from TALIS ({{when}}) was refused. The articles are as they were before.',
  'quotationLists.codeInTalis':
    'An article from TALIS has this code too. That is allowed, but then the code is in the picker twice.',
  'quotationLists.talisArticleTitle': 'Article from TALIS',
  'quotationLists.talisReadOnly':
    'This article comes from TALIS and can only be read here. Change it in TALIS; the link takes it over within the hour. In a quotation you adjust the sizes per line.',
  'quotationLists.talisInactive':
    'This article is no longer in TALIS, or is historical there, so it cannot be chosen in a new quotation. A quotation that has it, keeps it.',
  'quotationLists.field.wmsClient': 'Client (WMS)',
  'quotationLists.field.articleGroup': 'Article group',
  'quotationLists.field.lastReceived': 'Last received',
  'quotationLists.importAlsoInTalis': 'Also in TALIS',
  'quotationLists.importAlsoInTalisNote_one':
    '{{count}} new article has the code of an article from TALIS. That is allowed, but then that code is in the picker twice.',
  'quotationLists.importAlsoInTalisNote_other':
    '{{count}} new articles have the code of an article from TALIS. That is allowed, but then those codes are in the picker twice.',
  'quotations.moreArticles_one': '{{count}} more article: keep typing to search.',
  'quotations.moreArticles_other': '{{count}} more articles: keep typing to search.',
  'quotations.event.createdFromRequest': 'Quotation created from a request',
  'quotations.read.title': 'New quotation',
  'quotations.read.sub':
    "Paste the customer's request. VHT fills in what it recognises; you check it, then make the concept.",
  'quotations.read.requestTitle': "Customer's request",
  'quotations.read.requestSub': 'Read in VHT; nothing goes out',
  'quotations.read.pasteLabel': "Paste the customer's mail",
  'quotations.read.pastePlaceholder': 'Please send me a price for transport from …',
  'quotations.read.read': 'Read',
  'quotations.read.reading': 'Reading…',
  'quotations.read.readFailed': 'Reading did not work. Try again.',
  'quotations.read.otherRequest': 'Another request',
  'quotations.read.rereadHint': 'Reading again replaces what you changed next to it.',
  'quotations.read.markedWord': 'marked',
  'quotations.read.struckWord': 'struck through',
  'quotations.read.legendTaken': 'taken',
  'quotations.read.legendSkipped': 'recognised, not taken',
  'quotations.read.kept':
    'Kept with the quotation, under Internal. Not in the quotation and not in a mail.',
  'quotations.read.emptyTitle': 'Nothing read yet',
  'quotations.read.emptyText':
    "Paste the request in the field Customer's request and click Read. What VHT recognises then shows here to check.",
  'quotations.read.withoutRequest': 'Start without a request',
  'quotations.read.card.customer': 'Customer',
  'quotations.read.card.places.TRANSPORT': 'Loading and unloading',
  'quotations.read.card.places.HOISTING': 'Unloading',
  'quotations.read.card.placesSub.TRANSPORT': 'Where it comes from and where it goes',
  'quotations.read.card.placesSub.HOISTING': 'Where it is lifted',
  'quotations.read.card.cargo': 'Cargo',
  'quotations.read.card.cargoSub': 'The article, with the sizes and weight of this quotation',
  'quotations.read.card.lines': 'Lines',
  'quotations.read.card.linesSub': 'What the workbench gets',
  'quotations.read.card.skipped': 'Not taken',
  'quotations.read.card.skippedSub':
    'Recognised in the request, but deliberately not in the quotation',
  'quotations.read.skip.date': 'The date is up to the planning.',
  'quotations.read.source.REQUEST': 'From the request.',
  'quotations.read.source.VHT': 'From VHT.',
  'quotations.read.source.ASSUMED': 'Assumed.',
  'quotations.read.source.MISSING': 'Not found.',
  'quotations.read.source.USER': 'Changed.',
  'quotations.read.note.user': 'By you, after reading.',
  'quotations.read.note.quoted': '“{{text}}”.',
  'quotations.read.note.relationNotFound': 'Choose the relation, or make a prospect.',
  'quotations.read.note.relationByContact': '{{email}} is a contact person of {{relation}}.',
  'quotations.read.note.relationByDomain': 'The mail domain {{domain}} belongs to {{relation}}.',
  'quotations.read.note.relationByName': 'The name “{{name}}” is in the request.',
  'quotations.read.note.contactNoSender': 'No sender found.',
  'quotations.read.note.contactSender': 'The sender, {{email}}.',
  'quotations.read.note.contactSameName': '{{name}} is already with {{relation}}.',
  'quotations.read.note.contactNew':
    '{{name}} is not with {{relation}} yet. Name, phone and mail from the signature.',
  'quotations.read.note.contactNewNoRelation':
    '{{name}}, from the signature. Choose the relation first.',
  'quotations.read.note.typeWord': 'The request says “{{word}}”.',
  'quotations.read.note.typeAssumed': 'Nothing about the kind of work; transport assumed.',
  'quotations.read.note.referenceNone': 'No reference in the request.',
  'quotations.read.note.quantityAssumed': 'No quantity given; one assumed.',
  'quotations.read.note.sizesReadAsMm': '{{max}} {{unit}} cannot be, so millimetres.',
  'quotations.read.note.sizesReadAsCm': '{{max}} {{unit}} cannot be, so centimetres.',
  'quotations.read.note.sizesSameAsArticle': 'The same as the article.',
  'quotations.read.note.sizesDifferFromArticle': 'Differs from the article ({{sizes}}).',
  'quotations.read.note.sizesNotFound': 'No sizes found.',
  'quotations.read.note.weightNotFound': 'No weight found.',
  'quotations.read.note.fromArticle': 'From the article {{code}}.',
  'quotations.read.note.fromArticlePerPiece': 'From the article {{code}}, per piece.',
  'quotations.read.note.articleNotFound': 'Choose the article.',
  'quotations.read.note.articleCode': 'The code {{code}} is in the request.',
  'quotations.read.note.articleCodeTwice':
    'The code {{code}} is on the list {{count}} times; check it is the right one.',
  'quotations.read.note.articleClientWeight':
    '{{client}} has {{count}} articles; only this one weighs {{kg}} kg.',
  'quotations.read.note.articleClientNearest':
    '{{client}} has {{count}} articles; this one is closest to {{kg}} kg.',
  'quotations.read.note.articleWords': 'Chosen on the description; check it is the right one.',
  'quotations.read.note.placeNotFound': 'Not found in the request.',
  'quotations.read.note.placeAddressIsFixed': 'Fixed address: same street and place as “{{text}}”.',
  'quotations.read.note.placeAddress': 'The address is in the request.',
  'quotations.read.note.placeFixed': 'Fixed address; the request says “{{text}}”.',
  'quotations.read.note.placeClientsOnly':
    'The only fixed address of {{client}}; the request says “{{text}}”.',
  'quotations.read.note.placeCentre': 'Only a place, so the centre of {{city}} ({{country}}).',
  'quotations.read.note.placeUnknown': 'Unknown place “{{text}}”; check place and country.',
  'quotations.read.note.craneBoth': '“{{text}}”: two lines, the second with a crane and optional.',
  'quotations.read.note.craneNone': 'No crane mentioned.',
  'quotations.read.note.craneAtAssumed': 'Not said where; unloading assumed.',
  'quotations.read.mark.relation': 'relation',
  'quotations.read.mark.contact': 'contact person',
  'quotations.read.mark.type': 'type',
  'quotations.read.mark.reference': 'reference',
  'quotations.read.mark.article': 'article',
  'quotations.read.mark.quantity': 'quantity',
  'quotations.read.mark.sizes': 'sizes',
  'quotations.read.mark.weight': 'weight',
  'quotations.read.mark.load': 'loading',
  'quotations.read.mark.unload': 'unloading',
  'quotations.read.mark.crane': 'crane',
  'quotations.read.mark.date': 'not taken',
  'quotations.read.field.relationId': 'Relation',
  'quotations.read.field.contact': 'Contact person',
  'quotations.read.field.type': 'Type',
  'quotations.read.field.reference': 'Reference',
  'quotations.read.field.articleId': 'Article',
  'quotations.read.field.quantity': 'Quantity',
  'quotations.read.field.sizes': 'Sizes',
  'quotations.read.field.weightKg': 'Weight',
  'quotations.read.field.load': 'Loading',
  'quotations.read.field.unload': 'Unloading',
  'quotations.read.field.crane': 'Crane',
  'quotations.read.field.craneAt': 'Crane at',
  'quotations.read.newContact': 'New: {{name}}',
  'quotations.read.newContactHint':
    'Becomes a contact person of {{relation}} in VHT. If they are in TALIS, rather there.',
  'quotations.read.theRelation': 'the relation',
  'quotations.read.craneLabel': 'Crane',
  'quotations.read.crane.NONE': 'No crane',
  'quotations.read.crane.WITH': 'With crane',
  'quotations.read.crane.BOTH': 'With and without',
  'quotations.read.craneAtLabel': 'Crane at',
  'quotations.read.craneAt.LOAD': 'Loading',
  'quotations.read.craneAt.UNLOAD': 'Unloading',
  'quotations.read.at.LOAD': 'loading',
  'quotations.read.at.UNLOAD': 'unloading',
  'quotations.read.craneType': 'Crane type',
  'quotations.read.craneTypeLater': 'Choose later, in the workbench',
  'quotations.read.craneTypeHint': 'The request does not say which crane; you decide.',
  'quotations.read.lineWithout': 'without crane',
  'quotations.read.lineWith': 'with crane at {{at}}',
  'quotations.read.ownPrice': 'own price',
  'quotations.read.noArticle': 'article still to choose',
  'quotations.read.bothHint':
    'With and without a crane are two lines: the first without, the second with a crane and optional, each with its own price. So the customer sees the difference.',
  'quotations.read.assumedCount_one': 'One field is assumed. Check it; it is yellow.',
  'quotations.read.assumedCount_other':
    '{{count}} fields are assumed. Check them; they are yellow.',
  'quotations.read.create': 'Make concept',
  'quotations.read.creating': 'Making concept…',
  'quotations.read.noPrices': 'VHT suggests no prices and issues nothing.',
  'quotations.read.created': 'Concept {{number}} made from the request. Fill in the prices.',
  'quotations.read.createFailed': 'Making the concept did not work. Try again.',
  'quotations.read.intake': 'Filled in from the request.',
  'quotations.read.intakeOf': 'Filled in from the request of {{relation}}.',
  'quotations.read.intakeContact': '{{name}} was added as a contact person.',
  'quotations.read.intakeIntern': 'The request itself is under Internal.',
  'quotations.read.intakeCheck': 'Check what was assumed:',
  'quotations.read.intakeOk': 'Got it',
  'quotations.read.internTitle': "Customer's request",
  'quotations.read.internHint':
    'Pasted by {{who}}, {{when}}. Kept with the quotation, not only in the mail. Not in the quotation and not in a mail.',
  'quotations.guide.title': 'Help with this quotation',
  'quotations.guide.progress': '{{done}} of {{count}} done',
  'quotations.guide.now': 'now: {{step}}',
  'quotations.guide.doneRow': 'Done: {{steps}}',
  'quotations.guide.showAll': 'show all',
  'quotations.guide.showLess': 'Show less',
  'quotations.guide.more_one': 'and {{count}} more',
  'quotations.guide.more_other': 'and {{count}} more',
  'quotations.guide.goTo': 'Go to {{step}}',
  'quotations.guide.stillToDo_one': 'One thing still to do; the help says what.',
  'quotations.guide.stillToDo_other': '{{count}} things still to do; the help says which.',
  'quotations.guide.step.request': 'Request',
  'quotations.guide.step.customer': 'Customer',
  'quotations.guide.step.places.TRANSPORT': 'Loading and unloading',
  'quotations.guide.step.places.HOISTING': 'Unloading',
  'quotations.guide.step.cargo': 'Cargo',
  'quotations.guide.step.crane': 'Crane',
  'quotations.guide.step.route': 'Calculate',
  'quotations.guide.step.prices': 'Prices',
  'quotations.guide.step.texts': 'Text',
  'quotations.guide.step.issue': 'Check and issue',
  'quotations.guide.note.request': 'Read, and kept under Internal.',
  'quotations.guide.note.noRequest': 'Started without a request.',
  'quotations.guide.note.requestNow': 'Check the proposal, and make the concept.',
  'quotations.guide.note.customer': '{{relation}}, attn. {{contact}}.',
  'quotations.guide.note.customerShort': '{{relation}}',
  'quotations.guide.note.places': '{{trips}}.',
  'quotations.guide.note.cargo': '{{articles}}.',
  'quotations.guide.note.cargoLines': '{{articles}}, on {{lines}} lines.',
  'quotations.guide.note.noCrane': 'No crane in this quotation.',
  'quotations.guide.note.crane': '{{cranes}}.',
  'quotations.guide.note.noRoute': 'Hoisting has no route.',
  'quotations.guide.note.routeOff': 'Calculating is not possible here: HERE is off.',
  'quotations.guide.note.calculate': 'Calculate the route: kilometres, driving time and toll.',
  'quotations.guide.note.calculateLines':
    'Calculate line {{lines, list}}: kilometres, driving time and toll.',
  'quotations.guide.note.route': 'Route and toll calculated.',
  'quotations.guide.note.prices': 'Filled in.',
  'quotations.guide.note.pricesLines': 'Every line has its own price.',
  'quotations.guide.note.pricesLinesLater': 'In the workbench: its own price for every line.',
  'quotations.guide.note.texts': 'The standard texts are in; change them if needed.',
  'quotations.guide.note.textsDefault': 'The standard texts are in already.',
  'quotations.guide.note.issue': 'Check the sheet. After that the version is fixed.',
  'quotations.guide.note.later': 'In the workbench.',
  'quotations.route.truck': 'Truck',
  'quotations.route.noTrucks': 'No trucks in Quotation lists',
  'quotations.route.truckSpec': '{{kind}}, {{weight}} t, {{axles}} axles, Euro {{euro}}',
  'quotations.route.loadTime': 'Loading time (h)',
  'quotations.route.unloadTime': 'Unloading time (h)',
  'quotations.route.timesHint':
    'Loading and unloading time count in the time of the whole trip; no need to calculate again for them. Type 2, 1.5 or 1:30. Another truck does need calculating again: route and toll depend on it.',
  'quotations.route.legs.load': 'Loading',
  'quotations.route.legs.unload': 'Unloading',
  'quotations.route.error.NO_TRUCK': 'There is no truck in Quotation lists to calculate with.',
};
